import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Box, Card, CardContent, Typography, Grid, Chip, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Paper, Button, Divider, Collapse, IconButton, Tooltip, Alert,
  Tabs, Tab, Pagination, TextField, FormControl, InputLabel, Select, MenuItem, Stack,
  Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, Radio, FormLabel,
} from '@mui/material'
import {
  Description, Assessment, ExpandMore, ExpandLess, KeyboardArrowLeft,
  SmartToy, Shield, Memory, Hub, Article, Warning, CheckCircle, Download, Lock,
  ThumbUpAltOutlined, ThumbDownAltOutlined, FeedbackOutlined,
  AccountTree, Storage, Public, AltRoute, CallMade, CallReceived,
  CompareArrows, InfoOutlined,
} from '@mui/icons-material'
import { reachApi, ReachComponent } from '../services/api'
import { analysisApi, analyticsApi, reportApi } from '../services/api'
import MaturityRadar from '../components/MaturityRadar'
import Sparkline from '../components/Sparkline'
import AgentABFDetail from '../components/AgentABFDetail'
import AgentResilience from '../components/AgentResilience'
import DeploymentCard from '../components/DeploymentCard'
import AgentReaches from '../components/AgentReaches'
import CostFactorsBreakdown from '../components/CostFactorsBreakdown'
import PermissionFitBreakdown from '../components/PermissionFitBreakdown'
import DriftBadge from '../components/DriftBadge'
import UpgradeGate from '../components/UpgradeGate'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { useEntitlements } from '../hooks/useEntitlements'
import AgentBlastRadius from '../components/AgentBlastRadius'
import { useCounterfactualExposure } from '../hooks/useCounterfactualExposure'
import { useAttackPaths } from '../hooks/useAttackPaths'
import AgentRoutes from '../components/AgentRoutes'
import BundleLocked from '../components/BundleLocked'
import BundleGaps from '../components/BundleGaps'
import TelemetryGapsExport from '../components/TelemetryGapsExport'
import { MeshView, NodeDetailDrawer } from '../components/MeshView'
import type { MeshNode, MeshResponse, OverlayMode } from '../components/MeshView/types'

const DIMENSIONS = [
  { key: 'prompt_engineering', label: 'Prompt Engineering' },
  { key: 'agent_design', label: 'Agent Design' },
  { key: 'memory_architecture', label: 'Memory Architecture' },
  { key: 'orchestration_soundness', label: 'Orchestration' },
  { key: 'observability_tracing', label: 'Observability' },
  { key: 'governance_alignment', label: 'Governance' },
]

// Findings quality plan Phase 1a (docs/FINDINGS_QUALITY_PLAN.md).
// Mirrors SEVERITY_WEIGHT in services/analysis/main.py:2306. The findings
// endpoint already returns rows in this order, so severity-descending is
// the default sort here — re-sorting is a superset of the server's
// behaviour, never a replacement for it.
const SEVERITY_RANK: Record<string, number> = {
  critical: 4, high: 3, medium: 2, low: 1, info: 0,
}
const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info']

// Evidence strength: was this present in the source, or inferred from it.
//
// The column was "Confidence" showing a percentage, and the name made a
// promise the number does not keep. It is not how likely a finding is to be
// correct — it is how directly the finding was evidenced. 20 of 22 analyzers
// emit a single constant, so two findings of the same kind always carry the
// same number and it never moves when the customer's code changes.
//
// Bands rather than a percentage, because a reader cannot act differently on
// 55% versus 70%, and because a percentage invites averaging into a sentence
// nobody should say: "your findings are 76% accurate".
//
// Two bands, not three. A finding ARIAS chose to show is one it is asking the
// customer to look at, and labelling our own output "Low" argues against the
// finding on the same row that presents it.
//
// The cut at 0.75 falls on a real boundary in the analyzer priors rather than
// a round number. Above it sit maturity_check, spec_trace, security_risk and
// external_integration — all reading parsed structure or a literal string.
// Below it sit tool_quality, prompt_quality, guardrail_effectiveness and
// semantic_analysis — all inference. Measured on two production scans, that
// splits 45/54 and 59/40.
//
// 0.5 is the platform's own floor (shared.confidence.DEFAULT_CONFIDENCE) and
// is the lowest value present in production, so Medium covers everything that
// exists. A value beneath it would still be inference and still reads Medium,
// rather than leaving the column blank for a case that cannot occur today.
const EVIDENCE_BANDS = [
  { min: 0.75, label: 'High', help: 'Present in source code' },
  { min: 0.5, label: 'Medium', help: 'Inferred from source code' },
] as const

const evidenceBand = (v: number | null) =>
  v === null ? null : EVIDENCE_BANDS.find(b => v >= b.min) ?? EVIDENCE_BANDS[EVIDENCE_BANDS.length - 1]

// Phase 2e — mirrors LOW_CONFIDENCE_THRESHOLD in services/shared/confidence.py.
// Below this a finding is a lead rather than a result. The value only became
// meaningful once Phase 2 gave the heuristic analyzers honest priors; before
// that nothing in prod fell below it, so this filter had never hidden anything.

// What a customer can decide about a finding, in the customer's words.
//
// The values are OpenVEX, which GRC and SBOM tooling already speak, so a
// decision made here is legible to the tool it travels to. `fixed` is absent
// on purpose: it is an observation, not an assertion — ARIAS derives it from a
// scan that no longer reports the finding, and letting somebody declare it by
// hand would let a control pass on a promise.
const DECISION_STATUSES: Array<{ value: string; label: string; help: string }> = [
  { value: 'not_affected', label: 'Does not apply to us',
    help: 'Stops this counting against your compliance posture, and tells your GRC tool why.' },
  { value: 'affected', label: 'Confirmed — we will fix it',
    help: 'Records that you have reviewed and accepted the finding. It keeps counting until a scan shows it resolved.' },
  { value: 'under_investigation', label: 'Looking into it',
    help: 'Records that this is being reviewed. It keeps counting in the meantime.' },
]

// The reason a finding does not apply. OpenVEX requires a machine-readable
// label rather than prose alone, because prose cannot be automated on — and
// CISA treats these five as the minimum set.
const VEX_JUSTIFICATIONS: Array<{ value: string; label: string }> = [
  { value: 'component_not_present', label: 'The component is not present' },
  { value: 'vulnerable_code_not_present', label: 'The code in question is not present' },
  { value: 'vulnerable_code_not_in_execute_path', label: 'The code is never executed' },
  { value: 'vulnerable_code_cannot_be_controlled_by_adversary', label: 'It cannot be reached by an attacker' },
  { value: 'inline_mitigations_already_exist', label: 'A mitigation is already in place' },
]

const LOW_CONFIDENCE_THRESHOLD = 0.5

// Why a gated analyzer did not assess this scan, in words a customer can act
// on. The states come from services/analysis/analyzer_ledger.py; anything
// unlisted falls through to the raw value rather than being hidden.
const ANALYZER_STATE_LABELS: Record<string, string> = {
  disabled_by_config: 'not enabled',
  not_entitled: 'not included in your plan',
  no_input: 'nothing in this scan to assess',
  errored: 'failed to run',
}


// Phase 5a's ANALYZER_LABELS table lived here, mapping `analyzer_source` to a
// sentence describing how a finding was reached. It is gone with the
// provenance footer that rendered it: the labels name ARIAS's own detectors,
// which is internal provenance rather than something a customer acts on. The
// column is still written on every finding, so the mapping can come back if
// the audience for it ever becomes an internal view.

type FindingSortKey = 'title' | 'severity' | 'category' | 'framework' | 'confidence' | 'count'

// Column definitions drive both the header cells and the sort comparator,
// so a column can never render a sort control it doesn't actually support.
const FINDING_COLUMNS: Array<{
  key: FindingSortKey; label: string; align?: 'center'; numeric?: boolean
}> = [
  { key: 'title', label: 'Finding' },
  { key: 'severity', label: 'Severity' },
  { key: 'category', label: 'Category' },
  { key: 'framework', label: 'Framework' },
  // The name invites the obvious reading — "how sure is ARIAS this finding is
  // real" — and that is not what the number is. It is a property of the
  // DETECTION METHOD: how much of the claim was observed in the code versus
  // inferred from it. Two findings from the same analyzer carry the same
  // value, and it does not move when the customer's code changes.
  //
  // Saying so in a tooltip is the smallest honest fix. The larger one — bands
  // instead of a percentage, so nobody averages it into "your product is 76%
  // accurate" — is a separate decision.
  // No header tooltip: the name says what it measures, and the two chips
  // carry the rest in three words each.
  { key: 'confidence', label: 'Evidence Strength', align: 'center', numeric: true },
  { key: 'count', label: 'Occurrences', align: 'center', numeric: true },
]

// Keys must exactly match scanner-client/framework_detector.py
// ARCHITECTURE_PATTERNS (the canonical taxonomy — that's the only place
// agent['architecture_pattern'] is ever assigned). Parity enforced by
// scanner-client/tests/test_architecture_pattern_parity.py. Module-level
// so both the Architecture Patterns summary card and individual agent
// card tooltips describe the SAME pattern the same way.
const PATTERN_DESCRIPTIONS: Record<string, { what: string; risk: string; riskLevel: string }> = {
  'Basic Responder': { what: 'Simple prompt-in, response-out. No tools, no memory, no autonomy.', risk: 'Low — limited capability, but also limited governance visibility.', riskLevel: 'low' },
  'Tool-Augmented LLM': { what: 'LLM with access to external tools but no autonomous decision loop.', risk: 'Medium — tool access expands the blast radius beyond text generation.', riskLevel: 'medium' },
  'Tool Calling Agent': { what: 'Agent that autonomously selects which tools to invoke. The LLM decides what to call and when.', risk: 'Higher — autonomous tool selection means actions you didn\'t explicitly program.', riskLevel: 'high' },
  'Reactive Agent': { what: 'Agent that responds to inputs with autonomous decision-making but without external tool access. Makes choices based on its training and instructions.', risk: 'Medium — autonomous decisions without tools limit blast radius, but outputs can still influence downstream systems.', riskLevel: 'medium' },
  'Pipeline / Chain': { what: 'Fixed sequence of LLM calls. Each step feeds into the next in a predefined order.', risk: 'Lower — predictable execution flow, but errors cascade through the chain.', riskLevel: 'low' },
  'Router / Dispatcher': { what: 'An LLM that decides which sub-agent or chain to invoke based on input.', risk: 'Medium — LLM-driven routing can expose unintended capabilities if misrouted.', riskLevel: 'medium' },
  'ReAct / Tool Loop': { what: 'Think → act → observe cycles until a goal is met. Runs multiple steps autonomously.', risk: 'High — iterative loops with tool access can take unbounded actions without constraints.', riskLevel: 'high' },
  'Iterative Reasoning': { what: 'Agent that refines output through multiple LLM passes without external tools.', risk: 'Medium — bounded by iteration limits, but cost and latency can be unpredictable.', riskLevel: 'medium' },
  'Multi-Agent Orchestration': { what: 'Multiple specialized agents coordinating — delegating, collaborating, sharing context.', risk: 'High — inter-agent communication creates complex trust boundaries and data flows.', riskLevel: 'high' },
  'Multi-Agent with Shared Memory': { what: 'Multi-agent system with shared memory store. One agent\'s output directly influences another.', risk: 'Very high — shared memory creates implicit dependencies and cross-agent contamination risk.', riskLevel: 'critical' },
  'Autonomous Agent': { what: 'Long-running agent operating independently — scheduled, background, or daemon processes.', risk: 'Highest — operates without human oversight. Requires guardrails, monitoring, and kill switches.', riskLevel: 'critical' },
}

/**
 * A finding's title with the agent's own name taken out of it.
 *
 * A title does two jobs badly: it states what the rule found *and* identifies
 * which agent it fired on. The second belongs in the locations list, which
 * already carries it — leaving it in the title means one rule firing across
 * an estate reads as a different problem every time.
 *
 * Only the agent's *own* name is removed, matched exactly rather than by
 * pattern. Where a title quotes something else as well — a model version, the
 * delegate an agent escalates through — that something else is what
 * distinguishes one finding from another and has to keep doing so, so the
 * agent is replaced by an ellipsis rather than deleted and the rest of the
 * title still separates the rows.
 */
function normaliseFindingTitle(title: string, agentName?: string | null): string {
  if (!title || !agentName) return title || ''
  const own = `"${agentName}"`
  if (!title.includes(own)) return title
  const quoted = title.match(/"[^"]*"/g) || []
  if (quoted.length > 1) return title.replace(own, '\u2026')
  return title
    .replace(own, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.:;])/g, '$1')
    .trim()
}


// Guardrail detection reads the agent's own file. A control applied through a
// shared helper — `chat_model()` wrapping a client, a factory returning a
// configured model — is real and is not currently followed across the module
// boundary, so an empty list means "none visible here", not "none exist".
//
// Saying so on the card matters more than it looks. Centralising safety
// configuration is what a model-risk function asks for, so the estates that
// organise it best are the ones this under-reports, and an unqualified "no
// guardrails" reads as a finding rather than a limit of the measurement.
// Scoped for a fix in docs/GUARDRAIL_ABSTRACTION_DETECTION_PLAN.md.
const GUARDRAILS_NONE_HELP =
  'No guardrail was found in this agent\'s own file. Controls applied through a ' +
  'shared helper or client factory in another module are not yet followed across ' +
  'files, so this means "none visible here" rather than "none exist" — check ' +
  'whether the agent builds its model through a shared configuration before ' +
  'treating it as a gap.'

const GUARDRAILS_FOUND_HELP =
  'Controls found in this agent\'s own file: safety thresholds, timeouts, retry ' +
  'and iteration limits, output validation. Controls applied through a shared ' +
  'helper in another module are not yet followed across files, so this list is a ' +
  'floor, not a complete inventory.'

const ROLE_VERDICT_LABEL: Record<string, string> = {
  well_defined: 'Well defined',
  can_be_improved: 'Can be improved',
  ambiguous: 'Ambiguous',
  not_declared: 'Not declared',
  not_assessed: 'Not assessed',
}

// Deliberately never `error`. A verdict here is advice about legibility, not
// evidence that a control failed, and it must not read louder than the
// findings on the same card or compete with them for attention.
const ROLE_VERDICT_COLOR: Record<string, string> = {
  well_defined: 'success.main',
  can_be_improved: 'warning.main',
  ambiguous: 'warning.main',
  not_declared: 'text.disabled',
  not_assessed: 'text.disabled',
}

const ROLE_SIGNAL_LABEL: Array<[string, string, string]> = [
  ['scope_limited', 'Scope limited', 'States what the agent does not do — the boundary a reader needs to know it will not act outside.'],
  ['constraints', 'Constraints', 'States rules the agent must follow, not only what it is for.'],
  ['refusals', 'Refusals', 'Says when to decline a request rather than attempt it.'],
  ['escalation', 'Escalation', 'Routes to a human or a higher authority instead of deciding alone.'],
  ['jurisdiction', 'Jurisdiction', 'Names a regime or regulation the behaviour is bound by.'],
]

/**
 * The agent's stated purpose, described rather than quoted.
 *
 * Role text is a verbatim string literal from the customer's source and
 * routinely carries approval thresholds, escalation criteria and
 * jurisdiction rules, so from 2026-09-07 the scanner analyses it on the
 * customer's machine and sends only what is derived. Nothing shown here is
 * customer text: the verdict and the signals come from a platform
 * vocabulary, and `source_field` is the framework's own field name.
 *
 * Stored scans taken before that change still carry `role`, so they keep
 * rendering the way they always did rather than showing a blank card.
 */
function RoleIntent({ agent }: { agent: any }) {
  const meta = agent.role_metadata

  if (!meta) {
    // Legacy scan.
    if (agent.role && agent.role !== 'unknown') {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
          Role: {agent.role}
        </Typography>
      )
    }
    return (
      <Typography variant="body2" color="warning.main" sx={{ mb: 1, fontSize: '0.8rem' }}>
        No role defined — consider adding a role description for governance clarity
      </Typography>
    )
  }

  const verdict = meta.verdict || 'not_assessed'
  const tier = meta.authorised_tier?.tier

  if (!meta.declared) {
    return (
      <Typography variant="body2" color="warning.main" sx={{ mb: 1, fontSize: '0.8rem' }}>
        No role defined — consider adding a role description for governance clarity
      </Typography>
    )
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
          Intent:
        </Typography>
        <Tooltip title="How completely the agent's stated purpose defines its behaviour, judged against the knowledge base's system-prompt guidance. Advice, not a finding — it does not affect compliance posture.">
          <Typography variant="body2"
            sx={{ fontSize: '0.8rem', fontWeight: 600, color: ROLE_VERDICT_COLOR[verdict] }}>
            {ROLE_VERDICT_LABEL[verdict] || verdict}
          </Typography>
        </Tooltip>
        {meta.source_field && (
          <Tooltip title="The framework field that carried the text, so you know which line of your own repository to open.">
            <Chip label={`${meta.source_field}=`} size="small" variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem', fontFamily: 'monospace' }} />
          </Tooltip>
        )}
        {!!meta.length && (
          <Tooltip title="Length of the stated role. The text itself stays in your environment — only this measurement is transmitted.">
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
              {meta.length} chars
            </Typography>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
        {ROLE_SIGNAL_LABEL.map(([key, label, help]) => {
          const present = meta.signals?.[key]
          return (
            <Tooltip key={key} title={help}>
              <Chip
                label={`${label} ${present ? '\u2713' : '\u2014'}`}
                size="small" variant="outlined"
                sx={{
                  height: 18, fontSize: '0.65rem',
                  color: present ? 'success.main' : 'text.disabled',
                  borderColor: present ? 'success.light' : 'divider',
                }} />
            </Tooltip>
          )
        })}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {tier && (
          <Tooltip title={`The strongest capability the stated purpose authorises, from the verbs it uses${
            meta.authorised_tier?.evidence_terms?.length
              ? ` (${meta.authorised_tier.evidence_terms.join(', ')})` : ''
          }. Permission-fit analysis compares the tools actually granted against this ceiling.`}>
            <Chip label={`Authorises: ${tier}`} size="small" variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }} />
          </Tooltip>
        )}
        {meta.states_decision_criteria && (
          <Tooltip title="The role states quantitative decision criteria — a limit, threshold or approval ceiling. The knowledge base flags business rules in a system prompt as an intellectual-property exposure when combined with prompt extraction. The criteria themselves are not transmitted or stored.">
            <Chip label="States decision criteria" size="small" variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem', color: 'warning.main', borderColor: 'warning.light' }} />
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

const AnalysisDetailPage = () => {
  const { analysisId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [analysis, setAnalysis] = useState<any>(null)
  const [findings, setFindings] = useState<any[]>([])
  const [trialCap, setTrialCap] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [reportError, setReportError] = useState('')
  // Findings quality plan Phase 1c: keyed on the representative finding id,
  // not the array index. With an index, changing a filter or re-sorting
  // while a row was open silently expanded a DIFFERENT finding.
  const [expandedFinding, setExpandedFinding] = useState<string | null>(null)
  const [detailTab, setDetailTab] = useState(0)
  const { has: hasFeature } = useEntitlements()
  // ARIAS Exposure. One estate-wide call shared by every agent card below;
  // `byHint === null` means the route refused, and a hint absent from a
  // non-null map means not computed for that agent — never a zero radius.
  const { byHint: exposureByHint, loading: exposureLoading,
          notEntitled: exposureRefused } = useCounterfactualExposure()
  // Route verdicts, joined to the agent card on `agent_id_hint` — the same
  // shape `permission_fit` uses via mvpsFindingByAgentHint below.
  const { byAgent: routesByAgent, notEntitled: routesRefused } = useAttackPaths()
  const { user } = useSelector((state: RootState) => state.auth)
  const isPlatformAdmin = user?.type === 'admin'
  // Platform admins always see Topology, matching the backend gate's bypass.
  const showTopology = isPlatformAdmin || hasFeature('topology')
  // Escalation-path UI fix (2026-07-29) — set from the Findings tab's
  // "View escalation path" CTA, read by the Topology tab. Lives at this
  // level (not inside either tab) because it's set from one tab and read
  // by a sibling tab — same cross-tab-boundary reasoning as
  // findingsAgentFilter below.
  const [topologyFocusPath, setTopologyFocusPath] = useState<string[] | null>(null)
  const [agentPage, setAgentPage] = useState(1)
  const [agentSearch, setAgentSearch] = useState('')
  const [agentPatternFilter, setAgentPatternFilter] = useState('')
  const agentPageSize = 20
  const [findingCategoryFilter, setFindingCategoryFilter] = useState('')
  // Findings quality plan Phase 1a — severity is the primary triage axis and
  // was the one dimension the table couldn't filter on. Multi-select so
  // "critical + high" is one gesture.
  const [findingSeverityFilter, setFindingSeverityFilter] = useState<string[]>([])
  const [findingSort, setFindingSort] = useState<{ key: FindingSortKey; dir: 'asc' | 'desc' }>(
    { key: 'severity', dir: 'desc' }
  )
  // Phase 2e — defaults to unchecked. It defaulted to true, which meant a
  // filter was silently active from first paint; harmless while it hid nothing,
  // misleading now that low-confidence findings actually exist.
  const [hideWeakFindings, setHideWeakFindings] = useState(false)
  // UX6 — deep-link from mesh drawer's "View N findings →" CTA. When
  // set, the Findings tab pre-filters to findings whose agent_id_hint
  // matches (or whose affected_agent_id_hints include) this hint.
  const [findingsAgentFilter, setFindingsAgentFilter] = useState<{
    agentId: string
    agentName: string
  } | null>(null)
  const [reportSections, setReportSections] = useState({
    summary: true, maturity: true, agents: true, findings: true,
    remediation: true, compliance: true, kbMatches: true,
  })

  // Feedback dialog state. `feedbackByFindingId` caches the user's existing
  // feedback per finding_id so we can show the inline state without an
  // extra round-trip per row. The aggregated row uses its
  // `representative_finding_id` to look up / submit feedback.
  const [feedbackDialog, setFeedbackDialog] = useState<{
    findingId: string; existingId?: string; existingType?: string; existingReason?: string;
  } | null>(null)
  const [feedbackType, setFeedbackType] = useState<string>('false_positive')
  const [feedbackReason, setFeedbackReason] = useState<string>('')
  const [feedbackByFindingId, setFeedbackByFindingId] = useState<
    Record<string, { id: string; feedback_type: string; reason?: string } | null>
  >({})
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)

  // What the customer decided about a finding, in OpenVEX terms — distinct
  // from the feedback above, which says whether ARIAS got the detection right.
  // This says what they are doing about it, and it is the half that reaches
  // compliance: `not_affected` stops the finding counting against a control,
  // recomputes posture immediately, and travels to their GRC tool.
  //
  // Cached by the aggregated row's representative_finding_id, the same key
  // feedback uses, so a decision shows inline without a round-trip per row.
  const [decisionDialog, setDecisionDialog] = useState<{
    findingId: string; title: string
  } | null>(null)
  const [decisionStatus, setDecisionStatus] = useState<string>('not_affected')
  const [decisionJustification, setDecisionJustification] = useState<string>('component_not_present')
  const [decisionStatement, setDecisionStatement] = useState<string>('')
  const [decisionSubmitting, setDecisionSubmitting] = useState(false)
  const [decisionByFindingId, setDecisionByFindingId] = useState<
    Record<string, { status: string; justification?: string } | null>
  >({})

  // Trend overlay payload from analytics (sparklines, deltas, previous-scan
  // radar polygon). Optional — null when this is the first scan for the
  // workspace or when the lookup fails.
  const [trend, setTrend] = useState<any>(null)
  // What each agent on this page retrieves from, keyed by agent_id_hint.
  // Tenant-scoped on purpose: a store declared in an infrastructure
  // repository and read from here is exactly the relationship a
  // single-analysis payload cannot carry, and it is the one worth showing.
  // Empty for tenants without the entitlement — the endpoint 404s and the
  // section simply does not render.
  const [reachByAgent, setReachByAgent] = useState<Record<string, ReachComponent[]>>({})

  // Which gated analyzers assessed this scan. A Findings tab with no
  // permission-fit rows means one of two very different things — the analyzer
  // looked and the estate is sound, or it never ran — and until this was
  // returned the reader could not tell which. Null on scans that predate it.
  const [analyzerCoverage, setAnalyzerCoverage] = useState<{
    analyzers_considered?: number; analyzers_assessed?: number;
    analyzers?: Array<{ feature: string; label: string; state: string; findings?: number; detail?: string }>
  } | null>(null)

  // Findings-derived capabilities cannot be asked "were you refused?" the way
  // an endpoint can — an absent finding means either "looked and found
  // nothing" or "never ran". `analyzer_ledger` already records which, and
  // this is the set that was refused for want of an entitlement. Reusing it
  // rather than inventing a second mechanism: the findings list has reported
  // `not_entitled` honestly since it shipped, and the agent card did not.
  const refusedAnalyzers = new Set(
    (analyzerCoverage?.analyzers || [])
      .filter((a) => a.state === 'not_entitled')
      .map((a) => a.feature),
  )

  useEffect(() => {
    if (analysisId) {
      Promise.all([
        analysisApi.getAnalysis(analysisId).then(r => setAnalysis(r.data)),
        analysisApi.getAnalysisFindings(analysisId).then(r => {
          const data = r.data
          setFindings(data.findings || data || [])
          setTrialCap(data.trial_cap || null)
          setAnalyzerCoverage(data.analyzer_coverage || null)
        }),
      ]).catch(console.error).finally(() => setLoading(false))
    }
  }, [analysisId])

  // Separate from the main load, and deliberately not awaited with it: Agent
  // Reach is a separate entitlement, so a 404 here is the ordinary case and
  // must not delay or fail the page.
  useEffect(() => {
    let cancelled = false
    reachApi.overview()
      .then(r => {
        if (cancelled) return
        const byAgent: Record<string, ReachComponent[]> = {}
        for (const component of r.data.components || []) {
          for (const dependent of component.reached_by || []) {
            const list = byAgent[dependent.agent_id_hint] || []
            if (!list.some(c => c.identity === component.identity)) list.push(component)
            byAgent[dependent.agent_id_hint] = list
          }
        }
        setReachByAgent(byAgent)
      })
      .catch(() => setReachByAgent({}))
    return () => { cancelled = true }
  }, [])

  // §22 (2026-07-27) — re-fetch just the cost_estimate after a volume
  // assumption is saved/cleared, so cost_per_day_usd updates immediately
  // on this page without a full reload or a rescan.
  const refetchCostEstimate = () => {
    if (analysisId) {
      analysisApi.getAnalysis(analysisId).then(r => setAnalysis(r.data)).catch(console.error)
    }
  }

  // Fetch trend overlay once we know the workspace_path_hash. Deliberately
  // separate from the main load so a missing/404 trend (first scan, no
  // history yet) doesn't block the page from rendering.
  useEffect(() => {
    const hash = analysis?.workspace_path_hash
    if (!hash) return
    analyticsApi.getWorkspaceTrends(hash)
      .then(r => setTrend(r.data))
      .catch(() => setTrend(null))  // 404 on first scan is expected
  }, [analysis?.workspace_path_hash])

  // Deep-link support from the Cost Analysis page ("#agent-<agent_id_hint>")
  // — Agent Details is detailTab 0, the default, so no tab switch is needed.
  useEffect(() => {
    if (!analysis || !location.hash.startsWith('#agent-')) return
    setTimeout(() => {
      const el = document.getElementById(location.hash.slice(1))
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
  }, [analysis, location.hash])

  const handleGenerateReport = async () => {
    setGenerating(true)
    setReportError('')
    try {
      await reportApi.generateFromAnalysis(analysisId!, {
        title: `Analysis Report ${analysisId!.substring(0, 8)}`,
        // Organization and repository are different facts. This used to send
        // the repository name as the organization, so the report was headed
        // with the repo and carried the customer's name nowhere. The report
        // service resolves both authoritatively from the analysis and its
        // tenant — which organization a report is issued to should not be a
        // client's decision — so nothing is sent here.
      }, {
        'X-Include-Summary': String(reportSections.summary),
        'X-Include-Maturity': String(reportSections.maturity),
        'X-Include-Agents': String(reportSections.agents),
        'X-Include-Findings': String(reportSections.findings),
        'X-Include-Remediation': String(reportSections.remediation),
        'X-Include-Compliance': String(reportSections.compliance),
        'X-Include-KBMatches': String(reportSections.kbMatches),
      })
      navigate(`/reports`)
    } catch (e: any) {
      const detail = e.response?.data?.detail || e.message || 'Report generation failed'
      setReportError(typeof detail === 'string' ? detail : JSON.stringify(detail))
      console.error('Report generation failed:', e)
    } finally {
      setGenerating(false)
    }
  }

  const getSeverityColor = (s: string) => {
    const map: any = { critical: 'error', high: 'warning', medium: 'info', low: 'success' }
    return map[s?.toLowerCase()] || 'default'
  }

  const scoreColor = (score: number) => {
    if (score >= 8) return '#4caf50'
    if (score >= 5) return '#ff9800'
    return '#f44336'
  }

  if (loading) return <LinearProgress />
  if (!analysis) return <Typography variant="h6">Analysis not found</Typography>

  const dimensionScores = DIMENSIONS.map(d => ({
    ...d,
    score: analysis[`risk_score_${d.key}`] ?? 0,
  }))

  // Group findings by severity for summary
  const severityCounts: Record<string, number> = {}
  findings.forEach(f => {
    const s = f.severity?.toLowerCase() || 'unknown'
    severityCounts[s] = (severityCounts[s] || 0) + 1
  })

  // Group findings by framework
  const frameworkCounts: Record<string, number> = {}
  findings.forEach(f => {
    const fw = f.framework || 'general'
    frameworkCounts[fw] = (frameworkCounts[fw] || 0) + 1
  })

  // hint -> display name, resolved client-side from the agent inventory
  // already loaded for the Agents tab. The findings API returns
  // agent_id_hint but not agent_name directly on each finding — this is
  // the join. Findings-pipeline integrity fix (2026-07-29): used below
  // to disambiguate two different agents in the same file that share an
  // identical finding (title+severity+rule_id), which previously
  // rendered as the same file path repeated with nothing to tell them
  // apart — read as a dedup bug even when the underlying data was
  // correct (both agents genuinely have the issue).
  const agentNameByHint: Record<string, string> = {}
  ;(analysis.agent_inventory || []).forEach((a: any) => {
    if (a?.agent_id_hint && a?.agent_name && a.agent_name !== 'unnamed_agent') {
      agentNameByHint[a.agent_id_hint] = a.agent_name
    }
  })

  // Aggregate findings by title+severity (collapse duplicates).
  // We keep the first underlying finding_id (`representative_finding_id`) so
  // feedback submission targets a real DB record. Aggregation is a UI
  // concern; the backend stores feedback per finding_id, and any of the
  // underlying records carries the same rule_id which is what FP analytics
  // group on.
  const aggregatedFindings: Array<{
    title: string; severity: string; category: string; framework: string;
    description: string; recommendation: string; rule_id: string;
    // Phase 2c — nullable. A finding with no recorded confidence renders as
    // "—", never as a number this page invented for it.
    kb_similarity: number | null; confidence: number | null; count: number;
    // Phase 4e — before/after code the API resolves from the static
    // recommendations table at read time.
    //
    // Still `string` here, not a map: a rule may carry several variants keyed
    // by framework and language, but the server picks the one that fits this
    // finding. Resolving client-side would mean the browser holding a second
    // copy of the framework-family rules, and two copies of a precedence order
    // is how they come to disagree.
    code_example_bad?: string | null; code_example_good?: string | null;
    // One sentence naming the property the fix has to preserve. Absent on the
    // ~1,410 examples authored before it was required.
    principle?: string | null;
    // Phase 5a — which detector produced this.
    native_rule_id?: string | null; rule_group?: string | null;
    finding_kind?: string | null;
    recommendation_visible: boolean;
    locations: Array<{
      file_path: string; line_number: number | null;
      agent_id_hint: string | null; agent_name: string | null;
    }>;
    representative_finding_id: string;
    /**
     * Every agent a finding is about, when it is about more than one.
     *
     * A cross-agent or semantic finding names a count in its title — "7 agents
     * have multi-agent delegation", "Agent (20 agents) has no LLM cost
     * tracking" — and the agents themselves were carried on the finding and
     * rendered nowhere. A reader was told how many and never which, which
     * makes a true finding unactionable.
     */
    affected_agents: string[];
    // Escalation-path UI fix (2026-07-29) — only present on IACD-PERM-001
    // findings (see services/analysis/main.py's native_rule_id-gated
    // allowlist). IACD-PERM-001 findings are inherently per-agent/
    // unique-titled, so no meaningful dedup-collapse concern here the
    // way there could be for a genuinely duplicated finding type.
    agent_id_hint?: string | null;
    escalation_path?: string[] | null;
    via_agent_id_hint?: string | null;
    via_agent_name?: string | null;
  }> = []
  const findingGroups: Record<string, typeof aggregatedFindings[0]> = {}
  // UX6 — agent filter applies pre-aggregation so counts + locations
  // reflect ONLY the selected agent's slice. A finding qualifies if the
  // selected agent is either its primary agent_id_hint or appears in
  // its affected_agent_id_hints (multi-agent semantic findings).
  const findingsForAggregation = findingsAgentFilter
    ? findings.filter(f => {
        if (f.agent_id_hint === findingsAgentFilter.agentId) return true
        const affected = f.affected_agent_id_hints || []
        return Array.isArray(affected) && affected.includes(findingsAgentFilter.agentId)
      })
    : findings
  findingsForAggregation.forEach(f => {
    // Two findings are the same finding when the same rule says the same
    // thing at the same severity. Both halves of that were previously wrong.
    //
    // The title carried the agent's name — `Agent "triager" operates
    // autonomously without max iterations` — so one rule firing on seven
    // agents produced seven rows instead of one row counted seven times.
    // Whether a rule interpolates the agent or not is a property of the rule,
    // which is why the tab looked inconsistent between frameworks: both had
    // both kinds.
    //
    // And the key used `rule_id`, which is the *semantically matched* KB
    // rule. That match is computed per finding from its text, so seven
    // instances of one rule matched up to nine different KB rules and split
    // again. `native_rule_id` is the emitting rule's own id and is the same
    // for every instance by construction.
    const groupTitle = normaliseFindingTitle(
      f.title, f.agent_id_hint ? agentNameByHint[f.agent_id_hint] : null,
    )
    // The remediation is part of what makes two findings the same finding. A
    // grouped row is a claim that one fix settles every occurrence, so two
    // occurrences carrying different advice are two problems and belong on
    // two rows — not on one row with a caveat attached.
    //
    // Compared with the agent's name removed, because `Confirm "triager"
    // delegating…` and `Confirm "ingest" delegating…` are one instruction
    // applied twice, not two instructions.
    const groupAdvice = normaliseFindingTitle(
      (f.recommendation || '').trim(),
      f.agent_id_hint ? agentNameByHint[f.agent_id_hint] : null,
    )
    // `rule_group` is an opaque, per-scan stand-in for the rule that produced
    // the finding. The identity itself is withheld from this response — it
    // names ARIAS internals and a customer cannot act on it — but two findings
    // from different rules are still different problems, so the grouping needs
    // the distinction even without the name.
    const key = [
      groupTitle, f.severity,
      f.rule_group || f.native_rule_id || f.rule_id || '', groupAdvice,
    ].join('||')
    if (!findingGroups[key]) {
      findingGroups[key] = {
        // The grouped title, not this instance's. Showing one agent's name on
        // a row that stands for seven of them names the wrong six.
        title: groupTitle, severity: f.severity, category: f.category,
        framework: f.framework, description: f.description,
        recommendation: f.recommendation || '', rule_id: f.rule_id || '',
        kb_similarity: f.kb_similarity,
        // Phase 2c — was `f.confidence ?? 0.9`, which displayed a fabricated
        // 90% for any finding the backend had recorded nothing for. Null is
        // carried through and rendered as unknown.
        confidence: typeof f.confidence === 'number' ? f.confidence : null,
        code_example_bad: f.code_example_bad || null,
        code_example_good: f.code_example_good || null,
        principle: f.principle || null,
        native_rule_id: f.native_rule_id || null,
        count: 0, locations: [],
        recommendation_visible: f.recommendation_visible !== false,
        representative_finding_id: f.id,
        affected_agents: [],
        agent_id_hint: f.agent_id_hint || null,
        finding_kind: f.finding_kind || null,
        escalation_path: f.escalation_path || null,
        via_agent_id_hint: f.via_agent_id_hint || null,
        via_agent_name: f.via_agent_name || null,
      }
      aggregatedFindings.push(findingGroups[key])
    }
    findingGroups[key].count++
    // Track highest confidence across duplicates. A stated confidence always
    // beats an unknown one; unknowns never overwrite a stated value.
    if (typeof f.confidence === 'number') {
      const current = findingGroups[key].confidence
      if (current === null || f.confidence > current) {
        findingGroups[key].confidence = f.confidence
      }
    }
    // Names where the inventory knows them, the stable hint where it does not.
    // A hint is opaque but it is still an identifier a reader can search for,
    // and dropping the agent entirely because it has no display name is how
    // this became invisible in the first place.
    const affected = Array.isArray(f.affected_agent_id_hints)
      ? f.affected_agent_id_hints
      : []
    const named = (f.metadata?.affected_agents as string[] | undefined) || []
    const group = findingGroups[key]
    for (const value of (named.length >= affected.length ? named : affected)) {
      if (value && !group.affected_agents.includes(value)) {
        group.affected_agents.push(
          agentNameByHint[value] || value,
        )
      }
    }
    if (f.file_path) {
      findingGroups[key].locations.push({
        file_path: f.file_path, line_number: f.line_number,
        agent_id_hint: f.agent_id_hint || null,
        agent_name: f.agent_id_hint ? (agentNameByHint[f.agent_id_hint] || null) : null,
      })
    }
  })

  // -------------------------------------------------------------------------
  // Feedback handlers (Phase 1 of the continuous-improvement loop)
  // -------------------------------------------------------------------------
  const openFeedbackDialog = async (findingId: string) => {
    // Pre-load existing feedback if cached, else fetch
    let existing = feedbackByFindingId[findingId]
    if (existing === undefined) {
      try {
        const res = await analysisApi.getFindingFeedback(findingId)
        existing = res.data || null
        setFeedbackByFindingId(prev => ({ ...prev, [findingId]: existing }))
      } catch {
        existing = null
      }
    }
    setFeedbackType(existing?.feedback_type || 'false_positive')
    setFeedbackReason(existing?.reason || '')
    setFeedbackDialog({
      findingId,
      existingId: existing?.id,
      existingType: existing?.feedback_type,
      existingReason: existing?.reason,
    })
  }

  const openDecisionDialog = async (findingId: string, title: string) => {
    // Read the decision on record first, so reopening shows what was chosen
    // rather than the default. A 404 means none exists yet, which is ordinary.
    try {
      const res = await analysisApi.getFindingState(findingId)
      const st = res.data?.status
      if (st) {
        setDecisionStatus(st)
        setDecisionJustification(res.data?.justification || 'component_not_present')
        setDecisionStatement(res.data?.statement || '')
      }
    } catch {
      setDecisionStatus('not_affected')
      setDecisionJustification('component_not_present')
      setDecisionStatement('')
    }
    setDecisionDialog({ findingId, title })
  }

  const submitDecision = async () => {
    if (!decisionDialog) return
    setDecisionSubmitting(true)
    try {
      await analysisApi.setFindingState(decisionDialog.findingId, {
        status: decisionStatus as 'under_investigation' | 'affected' | 'not_affected',
        // Only not_affected carries a justification, and the server rejects
        // one where it does not apply.
        justification: decisionStatus === 'not_affected' ? decisionJustification : undefined,
        statement: decisionStatement || undefined,
      })
      setDecisionByFindingId(prev => ({
        ...prev,
        [decisionDialog.findingId]: {
          status: decisionStatus,
          justification: decisionStatus === 'not_affected' ? decisionJustification : undefined,
        },
      }))
      setDecisionDialog(null)
      setDecisionStatement('')
    } catch (e) {
      // Left open so the user can correct and retry — a decision that reaches
      // compliance and a GRC tool should not fail silently.
    } finally {
      setDecisionSubmitting(false)
    }
  }

  const submitFeedback = async () => {
    if (!feedbackDialog) return
    setFeedbackSubmitting(true)
    try {
      const res = await analysisApi.submitFindingFeedback(feedbackDialog.findingId, {
        feedback_type: feedbackType,
        reason: feedbackReason || undefined,
      })
      setFeedbackByFindingId(prev => ({
        ...prev,
        [feedbackDialog.findingId]: { id: res.data.id, feedback_type: feedbackType, reason: feedbackReason },
      }))
      setFeedbackDialog(null)
    } catch (e) {
      // Surfacing errors via the dialog itself would be nice — for v1 a
      // silent retry is acceptable since the user can re-submit.
    } finally {
      setFeedbackSubmitting(false)
    }
  }

  const undoFeedback = async () => {
    if (!feedbackDialog?.existingId) return
    setFeedbackSubmitting(true)
    try {
      await analysisApi.deleteFindingFeedback(feedbackDialog.findingId, feedbackDialog.existingId)
      setFeedbackByFindingId(prev => ({ ...prev, [feedbackDialog.findingId]: null }))
      setFeedbackDialog(null)
    } finally {
      setFeedbackSubmitting(false)
    }
  }

  const FEEDBACK_LABELS: Record<string, string> = {
    false_positive: 'False positive',
    helpful: 'Helpful',
    severity_too_high: 'Severity too high',
    unclear: 'Unclear',
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button size="small" startIcon={<KeyboardArrowLeft />} onClick={() => navigate('/analyses')}
            sx={{ mb: 1 }}>
            Back to Analyses
          </Button>
          <Typography variant="h4">
            {analysis.repository_name && analysis.repository_name !== 'unknown'
              ? analysis.repository_name
              : 'Analysis Results'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {analysis.analysis_id} &middot; {new Date(analysis.created_at).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* SD2 — Compare to another scan of the same repo. ScanDiffPage
              auto-picks the previous scan as baseline. */}
          {analysis.repo_identity_hash && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CompareArrows />}
              onClick={() =>
                navigate(
                  `/diff/${encodeURIComponent(analysis.repo_identity_hash)}?b=${encodeURIComponent(analysis.analysis_id)}`,
                )
              }
            >
              Compare to previous scan
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Description />}
            onClick={handleGenerateReport}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate Report'}
          </Button>
        </Box>
      </Box>

      {reportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReportError('')}>
          {reportError}
        </Alert>
      )}

      {/* Summary Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', py: 2, '&:last-child': { pb: 2 } }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Findings</Typography>
            <Typography variant="h5">{findings.length}</Typography>
          </Box>
          {['critical', 'high', 'medium', 'low'].map(sev => (
            severityCounts[sev] ? (
              <Box key={sev}>
                <Typography variant="caption" color="text.secondary">{sev.charAt(0).toUpperCase() + sev.slice(1)}</Typography>
                <Typography variant="h5" sx={{ color: sev === 'critical' || sev === 'high' ? '#f44336' : sev === 'medium' ? '#ff9800' : '#4caf50' }}>
                  {severityCounts[sev]}
                </Typography>
              </Box>
            ) : null
          ))}
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">Agents Detected</Typography>
            <Typography variant="h5" sx={{ color: '#1976d2' }}>
              {analysis.agents_detected ?? 0}
            </Typography>
          </Box>
          {analysis.agents_by_framework && Object.keys(analysis.agents_by_framework).length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Agents by Framework</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                {Object.entries(analysis.agents_by_framework).map(([fw, count]: [string, any]) => (
                  <Chip key={fw} label={`${fw}: ${count}`} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">Frameworks</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {(analysis.frameworks_detected || Object.keys(frameworkCounts)).map((fw: string) => (
                <Chip key={fw} label={fw} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Overall Score + Dimension Radar + Trust Pillars */}
      {(() => {
        const ds = (key: string) => analysis[`risk_score_${key}`] ?? 0
        // Previous-scan dimension lookup. trend.previous_assessment is the
        // assessment immediately before the current one (or null on first
        // scan). Used for delta arrows on the score cards + faded second
        // polygon on the radar.
        const prevDims: Record<string, number> | null =
          trend?.previous_assessment?.dimension_scores || null
        const dsPrev = (key: string) => prevDims ? (prevDims[key] ?? 0) : null
        const overallSeries: number[] =
          (trend?.assessments || []).map((a: any) => a.overall_score)
        const overallDelta = trend?.previous_assessment
          ? (analysis.risk_score_overall || 0) - (trend.previous_assessment.overall_score || 0)
          : null
        const fmtDelta = (d: number) => `${d >= 0 ? '↑' : '↓'} ${Math.abs(d).toFixed(1)}`
        const deltaColor = (d: number) => d > 0.05 ? '#4caf50' : d < -0.05 ? '#f44336' : '#9e9e9e'

        const computePillar = (formula: (g: (k: string) => number) => number) => formula(ds)
        const computePillarPrev = (formula: (g: (k: string) => number) => number) =>
          prevDims ? formula((k: string) => prevDims[k] ?? 0) : null

        const trustPillars = [
          { key: 'safety', label: 'Safety', icon: '🛡',
            formula: (g: (k: string) => number) => g('governance_alignment') * 0.4 + g('agent_design') * 0.3 + g('orchestration_soundness') * 0.3,
            desc: 'Guardrails, human oversight, capability risk' },
          { key: 'reliability', label: 'Reliability', icon: '⚡',
            formula: (g: (k: string) => number) => g('agent_design') * 0.35 + g('memory_architecture') * 0.3 + g('orchestration_soundness') * 0.35,
            desc: 'Behavioral consistency, error handling, drift stability' },
          { key: 'robustness', label: 'Robustness', icon: '🔒',
            formula: (g: (k: string) => number) => g('prompt_engineering') * 0.3 + g('agent_design') * 0.3 + g('observability_tracing') * 0.2 + g('governance_alignment') * 0.2,
            desc: 'Prompt defenses, tool validation, edge case handling' },
          { key: 'transparency', label: 'Transparency', icon: '👁',
            formula: (g: (k: string) => number) => g('observability_tracing') * 0.4 + g('governance_alignment') * 0.4 + g('prompt_engineering') * 0.2,
            desc: 'Audit trail, policy alignment, decision traceability' },
        ].map(p => ({
          ...p,
          score: computePillar(p.formula),
          prevScore: computePillarPrev(p.formula),
        }))

        // Build previous-scan dimension series in the same order as
        // dimensionScores so MaturityRadar can overlay them point-for-point.
        const previousDimensionScores = prevDims
          ? dimensionScores.map(d => ({ key: d.key, label: d.label, score: prevDims[d.key] ?? 0 }))
          : undefined

        return (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>Overall Score</Typography>
                <Typography variant="h1" sx={{ color: scoreColor(analysis.risk_score_overall || 0), fontWeight: 700 }}>
                  {(analysis.risk_score_overall || 0).toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">out of 10.0</Typography>
                {overallDelta !== null && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: deltaColor(overallDelta), fontWeight: 600 }}>
                    {fmtDelta(overallDelta)} vs last scan
                  </Typography>
                )}
                {overallSeries.length >= 2 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Sparkline values={overallSeries} width={120} height={28} color={scoreColor(analysis.risk_score_overall || 0)} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  6-Dimension Maturity
                  {previousDimensionScores && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (current vs previous scan)
                    </Typography>
                  )}
                </Typography>
                <MaturityRadar scores={dimensionScores} previousScores={previousDimensionScores} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Trust Pillars</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Governance view — derived from the 6 dimensions
                </Typography>
                {trustPillars.map(p => {
                  const delta = p.prevScore !== null ? p.score - p.prevScore : null
                  return (
                    <Box key={p.key} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {p.icon} {p.label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {delta !== null && Math.abs(delta) >= 0.05 && (
                            <Typography variant="caption" sx={{ color: deltaColor(delta), fontWeight: 600 }}>
                              {fmtDelta(delta)}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 700, color: scoreColor(p.score) }}>
                            {p.score.toFixed(1)}
                          </Typography>
                        </Box>
                      </Box>
                      <LinearProgress variant="determinate" value={p.score * 10}
                        sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { bgcolor: scoreColor(p.score), borderRadius: 4 } }} />
                      <Typography variant="caption" color="text.secondary">{p.desc}</Typography>
                    </Box>
                  )
                })}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        )
      })()}

      {/* Dimension Score Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {dimensionScores.map(d => {
          // Per-dimension trend strip — values pulled from each historical
          // assessment's dimension_scores in chronological order. Hidden
          // when there's only one data point.
          const series: number[] = (trend?.assessments || [])
            .map((a: any) => a.dimension_scores?.[d.key])
            .filter((v: any) => typeof v === 'number')
          const prevDimScore: number | undefined =
            trend?.previous_assessment?.dimension_scores?.[d.key]
          const delta = typeof prevDimScore === 'number' ? d.score - prevDimScore : null
          return (
            <Grid item xs={6} sm={4} md={2} key={d.key}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" noWrap>{d.label}</Typography>
                  <Typography variant="h5" sx={{ color: scoreColor(d.score), fontWeight: 600 }}>
                    {d.score.toFixed(1)}
                  </Typography>
                  {delta !== null && Math.abs(delta) >= 0.05 && (
                    <Typography variant="caption" sx={{
                      display: 'block',
                      color: delta > 0 ? '#4caf50' : '#f44336',
                      fontWeight: 600,
                    }}>
                      {delta >= 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}
                    </Typography>
                  )}
                  {series.length >= 2 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      <Sparkline values={series} width={70} height={20} color={scoreColor(d.score)} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      {/* Architecture Patterns */}
      {analysis.agent_inventory?.length > 0 && (
        <DeploymentCard agents={analysis.agent_inventory} />
      )}

      {analysis.agent_inventory?.length > 0 && (() => {
        const patternDescriptions = PATTERN_DESCRIPTIONS
        const riskColor = (level: string) => level === 'critical' ? 'error.main' : level === 'high' ? 'warning.main' : level === 'medium' ? 'info.main' : 'success.main'
        const patternCounts: Record<string, number> = {}
        analysis.agent_inventory.forEach((a: any) => {
          // architecture_pattern is intentionally null for component-type
          // nodes (MCP/A2A servers) — they aren't an interaction pattern,
          // so they don't belong in this distribution at all. Falling back
          // to a.autonomy_level here used to leak raw internal codes like
          // "L1" into the UI; excluding them is correct, not a display gap
          // — these nodes are already surfaced via their agent_type chip.
          if (!a.architecture_pattern) return
          const p = a.architecture_pattern
          patternCounts[p] = (patternCounts[p] || 0) + 1
        })
        // How many agents these cards actually account for, and how many they
        // do not. The exclusion above is correct — an MCP or A2A server is a
        // protocol surface, not an interaction pattern — but it is invisible
        // to a reader, who sees 22 agents detected and 20 in these cards and
        // has no way to reconcile the two. The code comment explains it to a
        // developer; this explains it to the customer.
        const patternedCount = Object.values(patternCounts).reduce((n, c) => n + (c as number), 0)
        const totalAgents = analysis.agent_inventory.length
        const unpatterned = totalAgents - patternedCount
        const riskBg = (level: string) => level === 'critical' ? '#fce4ec' : level === 'high' ? '#fff3e0' : level === 'medium' ? '#e3f2fd' : '#e8f5e9'
        return (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <Hub sx={{ mr: 1, verticalAlign: 'middle' }} />
              Architecture Patterns ({Object.keys(patternCounts).length} types)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How the {patternedCount} agent{patternedCount === 1 ? '' : 's'} in this
              repository are built{unpatterned > 0 ? (
                <> — {unpatterned} of {totalAgents} {unpatterned === 1 ? 'is a' : 'are'} MCP or
                A2A server{unpatterned === 1 ? '' : 's'}, which provide tools to agents rather
                than following an interaction pattern of their own, and {unpatterned === 1 ? 'appears' : 'appear'} under
                Agents instead</>
              ) : ''}. Hover a pattern for what it means and the risk it carries.
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(patternCounts)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([pattern, count]) => {
                  const desc = patternDescriptions[pattern]
                  const level = desc?.riskLevel || 'medium'
                  return (
                    <Grid item xs={6} sm={4} md={3} key={pattern}>
                      <Box sx={{
                        perspective: '600px',
                        height: 140,
                        '& .flip-inner': {
                          position: 'relative', width: '100%', height: '100%',
                          transition: 'transform 0.5s ease',
                          transformStyle: 'preserve-3d',
                        },
                        '&:hover .flip-inner': { transform: 'rotateY(180deg)' },
                        '& .flip-front, & .flip-back': {
                          position: 'absolute', width: '100%', height: '100%',
                          backfaceVisibility: 'hidden', borderRadius: 2,
                          display: 'flex', flexDirection: 'column',
                          justifyContent: 'center', alignItems: 'center',
                          p: 1.5, boxSizing: 'border-box',
                        },
                        '& .flip-back': { transform: 'rotateY(180deg)' },
                      }}>
                        <Box className="flip-inner">
                          {/* Front — pattern name + count */}
                          <Box className="flip-front" sx={{
                            bgcolor: riskBg(level), border: 2, borderColor: riskColor(level),
                          }}>
                            <Typography variant="h3" sx={{ color: riskColor(level), fontWeight: 700 }}>
                              {count}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ textAlign: 'center', fontWeight: 600, mt: 0.5 }}>
                              {pattern}
                            </Typography>
                            <Chip label={level} size="small" sx={{ mt: 1, fontSize: '0.65rem', height: 20,
                              bgcolor: riskColor(level), color: 'white' }} />
                          </Box>
                          {/* Back — description + risk */}
                          <Box className="flip-back" sx={{
                            bgcolor: riskBg(level), border: 2, borderColor: riskColor(level),
                            justifyContent: 'flex-start', alignItems: 'flex-start', pt: 1.5,
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {pattern}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                              {desc?.what || 'Agent architecture pattern'}
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 'auto', color: riskColor(level), fontWeight: 600 }}>
                              {desc?.risk || ''}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  )
                })}
            </Grid>
          </Box>
        )
      })()}

      {/* Detail Tabs */}
      <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {/* Explicit `value` on every Tab: the panels below key off these
            numbers, and MUI otherwise assigns values by render order — so
            hiding a tab would silently shift every tab after it. */}
        <Tab value={0} label={`Agent Details (${analysis.agent_inventory?.length || 0})`} />
        {/* Phase 1d — the tab counted raw occurrences while the card header
            counted unique rows, so the two never agreed. Both now count the
            rows the user can actually see; the raw total is explained in the
            header's tooltip. */}
        <Tab value={1} label={`Findings (${aggregatedFindings.length})`} />
        {/* Governance (2) hidden until the surface is further developed. */}
        <Tab value={3} label="Export" />
        {showTopology && <Tab value={4} label="Topology" />}
        {/* There was an "Exposure" tab here for one commit. Removed: a tab
            named after a SKU couples the information architecture to the price
            list, which is the thing ARIAS_PACKAGING §2's depth split exists to
            avoid — Core observes, Exposure interprets, on the SAME object. The
            four Exposure features that shipped before this never had a tab;
            they attach to the agent (`permission_fit`) and to the findings list
            (`escalation_path`). Route verdicts now attach to the agent card the
            same way, and guardrail coverage — an estate-level statement with no
            single object — lives with the component inventory. */}
      </Tabs>

      {/* Tab 0: Agent Details */}
      {detailTab === 0 && analysis.agent_inventory?.length > 0 && (() => {
        // 'No pattern' groups component-type nodes (MCP/A2A servers) that
        // intentionally have no architecture_pattern — distinct from a raw
        // fallback to autonomy_level, which used to leak internal codes.
        const patterns: string[] = [...new Set(analysis.agent_inventory.map((a: any) => a.architecture_pattern || 'No pattern'))] as string[]
        // §22 — per-agent cost estimate lookup, keyed by agent_id_hint.
        // analysis.cost_estimate is already [] server-side for tenants
        // below the gated tier (see get_analysis in services/analysis),
        // so no extra visibility check is needed here beyond that.
        const costByAgentHint: Record<string, any> = {}
        for (const est of analysis.cost_estimate || []) {
          if (est.agent_id_hint) costByAgentHint[est.agent_id_hint] = est
        }
        // Findings-pipeline UI fix (2026-07-29) — MVPS-001 finding per
        // agent, keyed by agent_id_hint. Sourced from the already-fetched
        // findings array (no new fetch), same idiom as costByAgentHint.
        const mvpsFindingByAgentHint: Record<string, any> = {}
        for (const f of findings) {
          if (f.finding_kind === 'permission_fit' && f.agent_id_hint) mvpsFindingByAgentHint[f.agent_id_hint] = f
        }
        const filtered = analysis.agent_inventory.filter((a: any) => {
          const name = (a.agent_name || a.framework || '').toLowerCase()
          const pattern = a.architecture_pattern || 'No pattern'
          const matchSearch = !agentSearch || name.includes(agentSearch.toLowerCase()) ||
            (a.file_path || '').toLowerCase().includes(agentSearch.toLowerCase())
          const matchPattern = !agentPatternFilter || pattern === agentPatternFilter
          return matchSearch && matchPattern
        })
        const paged = filtered.slice((agentPage - 1) * agentPageSize, agentPage * agentPageSize)
        return (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  <SmartToy sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Agent Details ({filtered.length}{filtered.length !== analysis.agent_inventory.length ? ` of ${analysis.agent_inventory.length}` : ''})
                </Typography>
                {/* Once for the section, not once per card. The original note
                    here said a teaser on every card "would be an advert, and
                    the Exposure tab already carries one" — true at the time,
                    then the tab was removed and nothing replaced it, so a
                    tenant without Exposure saw a page that looked complete. */}
                {(exposureRefused || routesRefused) && (
                  <BundleLocked feature="attack_paths" />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField size="small" placeholder="Search agents..." value={agentSearch}
                  onChange={e => { setAgentSearch(e.target.value); setAgentPage(1) }}
                  sx={{ width: 200 }} />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Pattern</InputLabel>
                  <Select value={agentPatternFilter} label="Pattern"
                    onChange={e => { setAgentPatternFilter(e.target.value); setAgentPage(1) }}>
                    <MenuItem value="">All patterns</MenuItem>
                    {patterns.map((p: string) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Grid container spacing={2}>
              {paged.map((agent: any, i: number) => {
                const riskLevel = agent.autonomy === 'autonomous' && (!agent.guardrails || agent.guardrails.length === 0)
                  ? 'high'
                  : agent.autonomy === 'autonomous' ? 'medium'
                  : agent.guardrails?.length > 0 ? 'low' : 'medium'
                const riskColor = riskLevel === 'high' ? '#f44336' : riskLevel === 'medium' ? '#ff9800' : '#4caf50'
                return (
                  <Grid item xs={12} sm={6} md={4} key={agent.agent_id || i}
                    id={agent.agent_id_hint ? `agent-${agent.agent_id_hint}` : undefined}>
                    <Card variant="outlined" sx={{ borderLeft: 4, borderColor: riskColor, height: '100%' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        {/* Agent name + framework + pattern */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {agent.agent_name && agent.agent_name !== 'unnamed_agent'
                                ? agent.agent_name
                                : `${agent.framework} agent`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
                              {agent.file_path}{agent.line_number ? `:${agent.line_number}` : ''}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
                            <Chip label={agent.framework} size="small" color="primary" variant="outlined" />
                            {agent.definition_source && agent.definition_source !== 'code' && (
                              <Tooltip title={`Declared in a spec file (${agent.definition_source}), not instantiated in code`}>
                                <Chip label="spec" size="small" color="secondary" sx={{ fontSize: '0.65rem' }} />
                              </Tooltip>
                            )}
                            {agent.architecture_pattern && (() => {
                              const desc = PATTERN_DESCRIPTIONS[agent.architecture_pattern]
                              const tooltipText = desc
                                ? `${desc.what} Risk: ${desc.risk}${agent.autonomy_level ? ` (detected level: ${agent.autonomy_level})` : ''}`
                                : agent.autonomy_level ? `Detected level: ${agent.autonomy_level}` : 'Architecture pattern'
                              return (
                                <Tooltip title={tooltipText}>
                                  <Chip label={agent.architecture_pattern} size="small" color="secondary" variant="outlined"
                                    sx={{ fontSize: '0.65rem' }} />
                                </Tooltip>
                              )
                            })()}
                            {agent.agent_id_hint && (
                              <Tooltip title="View in topology">
                                <IconButton size="small" onClick={() => setDetailTab(4)}
                                  sx={{ p: 0.25 }}>
                                  <AccountTree sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>

                        {/* Stated intent — described, not quoted. See RoleIntent. */}
                        <RoleIntent agent={agent} />

                        {/* Key attributes */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          <Tooltip title="Which LLM model this agent uses">
                            <Chip label={agent.llm_model && agent.llm_model !== 'unknown'
                              ? agent.llm_model : 'Model not specified'}
                              size="small" variant="outlined"
                              color={agent.llm_model && agent.llm_model !== 'unknown' ? 'default' : 'warning'}
                              sx={{ fontSize: '0.7rem' }} />
                          </Tooltip>
                          <Tooltip title={agent.autonomy === 'human_in_loop'
                            ? 'This agent requires human approval before taking actions'
                            : agent.autonomy === 'autonomous'
                            ? 'This agent can take actions without human approval'
                            : 'Could not determine if human oversight is configured'}>
                            <Chip
                              label={agent.autonomy === 'human_in_loop' ? 'Human-in-loop' : agent.autonomy === 'autonomous' ? 'Autonomous' : 'No oversight detected'}
                              size="small"
                              color={agent.autonomy === 'autonomous' ? 'error' : agent.autonomy === 'human_in_loop' ? 'success' : 'warning'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                          <Tooltip title={agent.memory_type && agent.memory_type !== 'none' && agent.memory_type !== 'stateless'
                            ? 'This agent persists state between interactions'
                            : 'This agent does not retain memory between calls'}>
                            <Chip icon={<Memory sx={{ fontSize: 14 }} />}
                              label={agent.memory_type && agent.memory_type !== 'none' && agent.memory_type !== 'stateless'
                                ? agent.memory_type : 'No memory'}
                              size="small" variant="outlined"
                              sx={{ fontSize: '0.7rem' }} />
                          </Tooltip>
                          {agent.multi_agent && (
                            <Tooltip title="This agent participates in a multi-agent system">
                              <Chip icon={<Hub sx={{ fontSize: 14 }} />}
                                label="Multi-Agent" size="small" color="info"
                                sx={{ fontSize: '0.7rem' }} />
                            </Tooltip>
                          )}
                        </Box>

                        {/* ARIAS Exposure — what falls with this agent. The
                            card above says what the agent IS; this says what a
                            compromise of it would reach. Renders nothing at all
                            for an unentitled tenant: a teaser on every card
                            would be an advert, and the Exposure tab already
                            carries one. */}
                        {exposureByHint && agent.agent_id_hint && (
                          <AgentBlastRadius
                            exposure={exposureByHint[agent.agent_id_hint] ?? null}
                            loading={exposureLoading}
                          />
                        )}
                        {routesByAgent && agent.agent_id_hint && (
                          <AgentRoutes routes={routesByAgent[agent.agent_id_hint] ?? []} />
                        )}

                        {/* Tools */}
                        {agent.tools?.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Tools ({agent.tools.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                              {agent.tools.slice(0, 8).map((t: string) => (
                                <Chip key={t} label={t} size="small"
                                  sx={{ fontSize: '0.65rem', height: 20 }} variant="outlined" />
                              ))}
                              {agent.tools.length > 8 && (
                                <Chip label={`+${agent.tools.length - 8}`} size="small"
                                  sx={{ fontSize: '0.65rem', height: 20 }} />
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* Guardrails */}
                        <Box sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Shield sx={{ fontSize: 14, color: agent.guardrails?.length > 0 ? '#4caf50' : '#f44336' }} />
                            {agent.guardrails?.length > 0 ? (
                              <Tooltip title={GUARDRAILS_FOUND_HELP}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                                  {agent.guardrails.map((g: string) => (
                                    <Chip key={g} label={g.replace(/_/g, ' ')} size="small" color="success"
                                      sx={{ fontSize: '0.6rem', height: 18 }} variant="outlined" />
                                  ))}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box>
                                <Tooltip title={GUARDRAILS_NONE_HELP}>
                                  <Typography variant="caption" color="error.main"
                                    sx={{ fontWeight: 600, borderBottom: '1px dotted', cursor: 'help' }}>
                                    No guardrails detected in this file
                                  </Typography>
                                </Tooltip>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                  {agent.tools?.length > 0
                                    ? 'Recommended: output validation, rate limiting, max iterations, input sanitization'
                                    : agent.autonomy === 'autonomous'
                                    ? 'Recommended: human-in-the-loop approval, max iterations, timeout limits'
                                    : 'Recommended: output validation, error handling'}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* §22 — Cost & Token Estimate. Renders nothing when
                            the feature is off, the tenant is below the
                            gated tier (cost_estimate is [] server-side in
                            that case), or this agent's model couldn't be
                            priced. */}
                        {(() => {
                          const est = costByAgentHint[agent.agent_id_hint]
                          if (!est || !est.pricing_matched || !est.cost_per_request_usd) return null
                          const fmt = (v: number) => (v < 0.01 ? v.toFixed(4) : v.toFixed(2))
                          return (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Cost & Token Estimate
                              </Typography>
                              <Tooltip title={`Projected from architecture pattern, tool mix, guardrails, and this scan's call graph — a range, not a measured value. ${est.tokens_per_request.low}–${est.tokens_per_request.high} tokens/request. See factors below.`}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.3 }}>
                                  <Chip
                                    label={`$${fmt(est.cost_per_request_usd.low)}–$${fmt(est.cost_per_request_usd.high)} / request`}
                                    size="small" variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                  {est.cost_per_day_usd && (
                                    <Chip
                                      label={`$${fmt(est.cost_per_day_usd.low)}–$${fmt(est.cost_per_day_usd.high)} / day`}
                                      size="small" variant="outlined" color="info"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                              </Tooltip>
                              <CostFactorsBreakdown estimate={est} onVolumeAssumptionChanged={refetchCostEstimate} />
                            </Box>
                          )
                        })()}

                        {/* Findings-pipeline UI fix (2026-07-29) — Permission
                            Fit. Renders nothing when this agent has no
                            MVPS-001 finding (over-permissioning wasn't
                            detected, or the feature is off — the finding
                            simply won't exist). */}
                        {(() => {
                          const mvpsFinding = mvpsFindingByAgentHint[agent.agent_id_hint]
                          // Refused beats absent. Without this the section
                          // simply vanished, and a tenant without Exposure saw
                          // a card that looked complete — the same silence that
                          // made the whole bundle invisible.
                          if (!mvpsFinding && refusedAnalyzers.has('permission_fit')) {
                            return (
                              <BundleLocked
                                feature="permission_fit"
                                label="Access beyond stated role"
                              />
                            )
                          }
                          if (!mvpsFinding) return null
                          return (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Access beyond stated role
                              </Typography>
                              <PermissionFitBreakdown finding={mvpsFinding} />
                            </Box>
                          )
                        })()}

                        {/* Connectivity — MCP / A2A / delegation surfaces from
                            scanner-client Preps 2/3/4. Renders nothing when
                            no signals exist, so legacy agents look unchanged. */}
                        <AgentConnectivityChips agent={agent} knownAgents={analysis.agent_inventory} />

                        {/* How the agent behaves when something goes wrong.
                            Derived from signals the scanner already reports —
                            guardrails, autonomy, and the failure issues the
                            detectors raise — rather than from the ABF score,
                            which measured the surrounding file rather than the
                            agent. Rendered outside the `agent.abf` guard so
                            spec-defined agents, which carry a minimal
                            fingerprint, are covered too. */}
                        <Box sx={{ mt: 1 }}>
                          <AgentResilience agent={agent} />
                        </Box>

                        {/* What this agent retrieves from. The card has always
                            named the *kind* of memory an agent keeps and never
                            which store it actually is. */}
                        <AgentReaches components={reachByAgent[agent.agent_id_hint] || []} />

                        {/* ABF — show key insight upfront */}
                        {agent.abf && <AgentABFDetail abf={agent.abf} />}
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
            {filtered.length > agentPageSize && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={Math.ceil(filtered.length / agentPageSize)}
                  page={agentPage} onChange={(_, p) => setAgentPage(p)} />
              </Box>
            )}
          </CardContent>
        </Card>
        )
      })()}

      {/* Tab 1: Findings & KB Matches */}
      {detailTab === 1 && (<Box>

      {/* Insights */}
      {analysis.insights?.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Insights</Typography>
            {analysis.insights.map((insight: any, i: number) => (
              <Box key={i} sx={{ mb: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">{insight.dimension || insight.category}</Typography>
                <Typography variant="body2">{insight.message || insight.description || insight.text}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Findings quality plan Phase 1b — the Findings card used to be gated
          on aggregatedFindings.length > 0 with no else branch, so a scan that
          raised nothing rendered a Findings tab containing no findings UI at
          all. A clean result is a result and deserves to be stated. */}
      {aggregatedFindings.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {findingsAgentFilter
                ? `No findings attributed to ${findingsAgentFilter.agentName}`
                : 'No findings in this analysis'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {findingsAgentFilter
                ? 'Other agents in this scan may still have findings.'
                : 'This scan completed without raising any findings.'}
            </Typography>
            {findingsAgentFilter && (
              <Button size="small" sx={{ mt: 1.5 }} onClick={() => setFindingsAgentFilter(null)}>
                Show all agents
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Findings (aggregated) */}
      {aggregatedFindings.length > 0 && (() => {
        const categories = [...new Set(aggregatedFindings.map(f => f.category?.replace(/_/g, ' ')))] as string[]

        // Phase 1a — filter first, then sort. Order matters: the header counts
        // describe the filtered set, and sorting must never change membership.
        let filteredFindings = aggregatedFindings
        if (findingCategoryFilter) {
          filteredFindings = filteredFindings.filter(
            f => f.category?.replace(/_/g, ' ') === findingCategoryFilter
          )
        }
        if (findingSeverityFilter.length > 0) {
          filteredFindings = filteredFindings.filter(
            f => findingSeverityFilter.includes((f.severity || '').toLowerCase())
          )
        }
        if (hideWeakFindings) {
          // Unknown confidence is not the same claim as low confidence, so an
          // unrecorded value is never swept up by this filter.
          filteredFindings = filteredFindings.filter(
            f => f.confidence === null || f.confidence >= LOW_CONFIDENCE_THRESHOLD
          )
        }

        const dir = findingSort.dir === 'asc' ? 1 : -1
        const rank = (s: string) => SEVERITY_RANK[(s || '').toLowerCase()] ?? -1
        filteredFindings = [...filteredFindings].sort((a, b) => {
          switch (findingSort.key) {
            case 'severity':   return dir * (rank(a.severity) - rank(b.severity))
            // Unknown confidence sorts as -1 so it lands at the bottom on the
            // default worst-first ordering rather than masquerading as high.
            case 'confidence': return dir * ((a.confidence ?? -1) - (b.confidence ?? -1))
            case 'count':      return dir * (a.count - b.count)
            case 'category':   return dir * (a.category || '').localeCompare(b.category || '')
            case 'framework':  return dir * (a.framework || '').localeCompare(b.framework || '')
            default:           return dir * (a.title || '').localeCompare(b.title || '')
          }
        })

        // Only offer severities this analysis actually contains — an empty
        // filter option is a dead end the user has to discover by clicking.
        const severitiesPresent = SEVERITY_ORDER.filter(
          s => aggregatedFindings.some(f => (f.severity || '').toLowerCase() === s)
        )
        const weakCount = aggregatedFindings.filter(
          f => f.confidence !== null && f.confidence < LOW_CONFIDENCE_THRESHOLD
        ).length
        // "Active" means actually excluding something. hideWeakFindings
        // defaults to true but hides nothing when weakCount is 0, and
        // offering to clear a filter with no visible effect is just noise.
        const filtersActive = Boolean(findingCategoryFilter)
          || findingSeverityFilter.length > 0
          || Boolean(findingsAgentFilter)
          || (hideWeakFindings && weakCount > 0)
        const clearFilters = () => {
          setFindingCategoryFilter('')
          setFindingSeverityFilter([])
          setHideWeakFindings(false)
          setFindingsAgentFilter(null)
        }
        const sortHandler = (key: FindingSortKey) => () => setFindingSort(prev =>
          prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            // Text columns read naturally A→Z; severity/confidence/count are
            // most useful worst-first, matching the server's default order.
            : { key, dir: key === 'title' || key === 'category' || key === 'framework' ? 'asc' : 'desc' }
        )

        return (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {/* Phase 1d — one vocabulary. The heading counts the rows on
                    screen; the raw-occurrence number that used to sit beside
                    it (and disagree with the tab label) moves into the
                    tooltip, where it explains the collapse instead of
                    competing with it. */}
                <Typography variant="h6">
                  Findings ({filteredFindings.length}
                  {filteredFindings.length !== aggregatedFindings.length ? ` of ${aggregatedFindings.length}` : ''})
                </Typography>
                <Tooltip title={`${aggregatedFindings.length} unique findings, aggregated from ${findings.length} occurrences across all locations. Use the Occurrences column to see how many times each one appears.`}>
                  <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
                {findingsAgentFilter && (
                  <Chip
                    size="small"
                    label={`Agent: ${findingsAgentFilter.agentName}`}
                    onDelete={() => setFindingsAgentFilter(null)}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {filtersActive && (
                  <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none' }}>
                    Clear filters
                  </Button>
                )}
              </Stack>
              {/* A check that did not run, reported as the exception it is.
                  This was a row of chips naming all three gated checks, which
                  singled out three finding types for no reason a reader could
                  see — the other twenty-odd analyzers produce findings and got
                  nothing. Whether a check ran is a statement about scan
                  completeness, not about findings, so it lives with the file
                  coverage now and only the exception surfaces here. */}
              {(() => {
                const skipped = (analyzerCoverage?.analyzers || []).filter(a => a.state !== 'ran')
                if (!skipped.length) return null
                // Split by WHY. An entitlement refusal is a capability the
                // tenant could have, so it gets a teaser saying what it would
                // answer. Every other skip state — the estate gave it nothing
                // to read, we switched it off platform-wide, it broke — is not
                // a sales conversation, and dressing it as one would ask a
                // customer to buy something they already hold.
                //
                // This was one sentence listing all of them, which read as an
                // apology and told a reader nothing about what any of the
                // three would have shown.
                const refused = skipped.filter(a => a.state === 'not_entitled')
                const other = skipped.filter(a => a.state !== 'not_entitled')
                return (
                  <>
                    {refused.length > 0 && (
                      <BundleGaps features={refused.map(a => a.feature)} />
                    )}
                    {other.length > 0 && (
                      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          {other.map(a => a.label).join(', ')}{' '}
                          {other.length === 1 ? 'was' : 'were'} not run on this scan
                          {other.length === 1 && other[0].state !== 'no_input'
                            ? ` (${ANALYZER_STATE_LABELS[other[0].state] || other[0].state})`
                            : ''}, so {other.length === 1 ? 'its' : 'their'} absence
                          from the list below is not a clean result.
                        </Typography>
                      </Alert>
                    )}
                  </>
                )
              })()}

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {weakCount > 0 && (
                  <FormControlLabel
                    control={<Checkbox checked={hideWeakFindings} onChange={e => setHideWeakFindings(e.target.checked)} size="small" />}
                    label={<Typography variant="body2">Hide low-confidence ({weakCount})</Typography>}
                  />
                )}
                {/* Phase 1a — severity is the primary triage axis and was the
                    one dimension with no filter. Multi-select so
                    "critical + high" is a single gesture. */}
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Severity</InputLabel>
                  <Select
                    multiple
                    value={findingSeverityFilter}
                    label="Severity"
                    onChange={e => setFindingSeverityFilter(
                      typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                    )}
                    renderValue={(selected) => (selected as string[]).length === severitiesPresent.length
                      ? 'All severities'
                      : (selected as string[]).join(', ')}
                  >
                    {severitiesPresent.map(s => (
                      <MenuItem key={s} value={s}>
                        <Checkbox size="small" checked={findingSeverityFilter.includes(s)} />
                        <Chip label={s} size="small" color={getSeverityColor(s) as any} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {aggregatedFindings.filter(f => (f.severity || '').toLowerCase() === s).length}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Category</InputLabel>
                  <Select value={findingCategoryFilter} label="Category"
                    onChange={e => setFindingCategoryFilter(e.target.value)}>
                    <MenuItem value="">All categories</MenuItem>
                    {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={30}></TableCell>
                    {/* Phase 1a — every data column sorts. Definitions live in
                        FINDING_COLUMNS so a header can't advertise a sort the
                        comparator doesn't implement. */}
                    {FINDING_COLUMNS.map(col => (
                      <TableCell key={col.key} align={col.align}
                        sortDirection={findingSort.key === col.key ? findingSort.dir : false}>
                        <TableSortLabel
                          active={findingSort.key === col.key}
                          direction={findingSort.key === col.key ? findingSort.dir : 'desc'}
                          onClick={sortHandler(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center" width={120}>Your decision</TableCell>
                    <TableCell align="center" width={90}>Feedback</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Phase 1b — a filter combination that matches nothing used
                      to render bare table headers with no explanation. */}
                  {filteredFindings.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No findings match the current filters.
                        </Typography>
                        <Button size="small" onClick={clearFilters} sx={{ mt: 1, textTransform: 'none' }}>
                          Clear filters
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredFindings.map((f) => {
                    // Phase 1c — key on the finding's own identity, not the
                    // array index. The index shifts under filtering and
                    // sorting, which both broke React reconciliation and made
                    // the expanded row jump to a different finding.
                    const rowKey = f.representative_finding_id
                    const isExpanded = expandedFinding === rowKey
                    return (
                    <React.Fragment key={rowKey}>
                      <TableRow hover sx={{
                        cursor: f.recommendation_visible ? 'pointer' : 'default',
                        opacity: f.recommendation_visible ? 1 : 0.45,
                      }}
                        onClick={() => f.recommendation_visible && setExpandedFinding(isExpanded ? null : rowKey)}>
                        <TableCell>
                          {f.recommendation_visible ? (
                            <IconButton size="small">
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          ) : (
                            // Phase 1e — the lock used to be a bare icon with
                            // no explanation of why the row was dimmed.
                            <Tooltip title="Details for this finding are part of a paid plan. Upgrade to see what was detected and how to fix it.">
                              <Lock sx={{ fontSize: 18, color: 'text.disabled', ml: 1, cursor: 'help' }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">{f.title}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={f.severity} color={getSeverityColor(f.severity) as any} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {f.category?.replace(/_/g, ' ')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={f.framework || 'general'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          {/* Findings-pipeline integrity fix (2026-07-29): confidence
                              previously reused error/warning (the same red/amber
                              severity uses), so a medium-severity finding with high
                              confidence showed a red chip next to a blue severity
                              chip — red didn't consistently mean "severe" across the
                              row, which could misdirect a quick scan. Confidence now
                              stays in the neutral/grey palette at every value —
                              differentiated by fill + weight, not hue — so severity
                              remains the row's only color-coded signal. */}
                          {/* Phase 2c — an unrecorded confidence renders as
                              unknown. It used to render as "90%", a number
                              this page made up on the backend's behalf. */}
                          {(() => {
                            const band = evidenceBand(f.confidence)
                            if (!band) return (
                              <Tooltip title="No evidence strength was recorded for this finding.">
                                <Typography variant="body2" color="text.disabled" sx={{ cursor: 'help' }}>
                                  —
                                </Typography>
                              </Tooltip>
                            )
                            return (
                              <Tooltip title={band.help} arrow>
                                <Chip
                                  label={band.label}
                                  size="small"
                                  color="default"
                                  variant={band.label === 'High' ? 'filled' : 'outlined'}
                                  sx={{ fontWeight: band.label === 'High' ? 600 : 400, cursor: 'help' }}
                                />
                              </Tooltip>
                            )
                          })()}
                        </TableCell>
                        <TableCell align="center">
                          {/* Phase 1f — this chip was color="warning" (amber),
                              which put a second hue in a row the 2026-07-29
                              fix had deliberately reduced to one colour-coded
                              signal. A high occurrence count is notable, not
                              severe; weight carries it without competing with
                              the severity chip. */}
                          {f.count > 1 ? (
                            <Tooltip title={`Detected at ${f.count} locations — expand to see them`}>
                              <Chip label={`${f.count}x`} size="small" color="default"
                                sx={{ fontWeight: 600, cursor: 'help' }} />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2">1</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {/* What the customer decided. Separate from feedback:
                              feedback says whether ARIAS got the detection
                              right; this says what they are doing about it,
                              and only this reaches compliance posture and
                              their GRC tool. */}
                          {(() => {
                            const d = decisionByFindingId[f.representative_finding_id]
                            const label = d && DECISION_STATUSES.find(x => x.value === d.status)?.label
                            if (d) {
                              return (
                                <Tooltip title={`Click to change. Recorded as "${label}" and sent with your compliance evidence.`}>
                                  <Chip
                                    label={label || d.status}
                                    size="small"
                                    color={d.status === 'not_affected' ? 'default' : 'primary'}
                                    variant={d.status === 'not_affected' ? 'outlined' : 'filled'}
                                    onClick={() => openDecisionDialog(f.representative_finding_id, f.title)}
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                </Tooltip>
                              )
                            }
                            return (
                              <Tooltip title="Record what you have decided about this finding. It updates your compliance posture and travels to your GRC tool.">
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => openDecisionDialog(f.representative_finding_id, f.title)}
                                  sx={{ fontSize: '0.65rem', textTransform: 'none', minWidth: 0 }}
                                >
                                  Record
                                </Button>
                              </Tooltip>
                            )
                          })()}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const fb = feedbackByFindingId[f.representative_finding_id]
                            if (fb) {
                              return (
                                <Tooltip title={`Click to update — currently: ${FEEDBACK_LABELS[fb.feedback_type] || fb.feedback_type}`}>
                                  <Chip
                                    label={FEEDBACK_LABELS[fb.feedback_type] || fb.feedback_type}
                                    size="small"
                                    color={fb.feedback_type === 'helpful' ? 'success' : 'default'}
                                    onClick={() => openFeedbackDialog(f.representative_finding_id)}
                                    icon={fb.feedback_type === 'helpful' ? <ThumbUpAltOutlined fontSize="small" /> : <ThumbDownAltOutlined fontSize="small" />}
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                </Tooltip>
                              )
                            }
                            return (
                              <Tooltip title="Mark feedback (false positive / helpful / etc)">
                                <IconButton size="small" onClick={() => openFeedbackDialog(f.representative_finding_id)}>
                                  <FeedbackOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                          })()}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                          <Collapse in={isExpanded && f.recommendation_visible} timeout="auto">
                            <Box sx={{ py: 2, px: 2 }}>
                              {f.description && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    What was detected
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {f.description}
                                  </Typography>
                                </Box>
                              )}
                              {f.recommendation && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Recommended action
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {f.recommendation}
                                  </Typography>
                                </Box>
                              )}
                              {/* Phase 4e — concrete before/after code. These
                                  examples were authored long ago (in
                                  recommendations.py, and in the KB rules'
                                  remediation_detail across ~1,464 rules) and had
                                  never been rendered anywhere. A prose
                                  recommendation tells you what to change; this
                                  shows it, which is the difference between a
                                  finding you act on and one you file. KB
                                  examples win when both exist — they are
                                  matched to the rule rather than to the title. */}
                              {(() => {
                                // The matched rule's examples used to be looked
                                // up here, from kb_matches, keyed on rule_id.
                                // The server resolves that now — KB rule first,
                                // static table as fallback — so the pair on the
                                // finding is already the right one, and this no
                                // longer needs an identity the response withholds.
                                const bad = f.code_example_bad
                                const good = f.code_example_good
                                const principle = f.principle
                                if (!bad && !good) return null
                                return (
                                  <Box sx={{ mb: 2 }}>
                                    {/* Headed "Example" with columns "Avoid" and
                                        "Prefer", nothing here said what to DO with
                                        the snippet — whether it was a patch to apply
                                        or a shape to learn from — and neither column
                                        was tied to the finding above it.

                                        The labels now state each side's role. They
                                        deliberately stop short of claiming the left
                                        block is what the detector matched: for some
                                        rules it demonstrably is not, and that claim
                                        can only be made once the authoring guard
                                        enforces it. */}
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                      Example shape
                                    </Typography>
                                    <Grid container spacing={1.5} sx={{ mt: 0 }}>
                                      {bad && (
                                        <Grid item xs={12} md={good ? 6 : 12}>
                                          <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                                            What the problem looks like
                                          </Typography>
                                          <Box component="pre" sx={{
                                            m: 0, mt: 0.5, p: 1.5, borderRadius: 1,
                                            bgcolor: 'grey.50', borderLeft: 3, borderColor: 'error.main',
                                            fontFamily: 'monospace', fontSize: '0.75rem',
                                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                            overflowX: 'auto',
                                          }}>{bad}</Box>
                                        </Grid>
                                      )}
                                      {good && (
                                        <Grid item xs={12} md={bad ? 6 : 12}>
                                          <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                            What a fix looks like
                                          </Typography>
                                          <Box component="pre" sx={{
                                            m: 0, mt: 0.5, p: 1.5, borderRadius: 1,
                                            bgcolor: 'grey.50', borderLeft: 3, borderColor: 'success.main',
                                            fontFamily: 'monospace', fontSize: '0.75rem',
                                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                            overflowX: 'auto',
                                          }}>{good}</Box>
                                        </Grid>
                                      )}
                                    </Grid>
                                    {/* Which property mattered. Two code blocks show
                                        THAT something changed; only this says what
                                        the change has to preserve — and that is what
                                        survives an API moving, and what stops the
                                        snippet being copied into a different stack
                                        with the defect intact.

                                        Required on newly authored examples, never
                                        backfilled, so most findings will not carry
                                        one for some time. */}
                                    {principle && (
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                        <strong>Why this works</strong> &mdash; {principle}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                                      Illustrative and minimal &mdash; adapt to your own code.
                                      What matters is the shape, not the syntax.
                                    </Typography>
                                  </Box>
                                )
                              })()}
                              {/* Which agents, not just how many. A finding
                                  whose title says "7 agents" and whose body
                                  names none of them is a true statement a
                                  reader cannot act on: they know something is
                                  wrong and not where. The agents were on the
                                  finding all along — 30 of 413 findings in one
                                  scan carried more than one — and were never
                                  rendered. */}
                              {f.affected_agents.length > 1 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Agents affected ({f.affected_agents.length})
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                    {f.affected_agents.map((name, j) => (
                                      <Chip key={j} size="small" variant="outlined" label={name}
                                        sx={{ height: 20, fontSize: '0.7rem' }} />
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                              {f.count > 1 && f.locations.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Found in {f.count} locations
                                  </Typography>
                                  <Box sx={{ mt: 0.5, maxHeight: 120, overflow: 'auto' }}>
                                    {f.locations.map((loc, j) => (
                                      <Typography key={j} variant="body2"
                                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.8 }}>
                                        {loc.file_path}{loc.line_number ? `:${loc.line_number}` : ''}
                                        {loc.agent_name && (
                                          <Typography component="span" variant="body2"
                                            sx={{ fontFamily: 'inherit', fontSize: 'inherit', color: 'text.secondary' }}>
                                            {' '}— agent: {loc.agent_name}
                                          </Typography>
                                        )}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                              {f.count === 1 && f.locations.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="caption" color="text.secondary">Location</Typography>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    {f.locations[0].file_path}{f.locations[0].line_number ? `:${f.locations[0].line_number}` : ''}
                                    {f.locations[0].agent_name && (
                                      <Typography component="span" variant="body2"
                                        sx={{ fontFamily: 'inherit', fontSize: 'inherit', color: 'text.secondary' }}>
                                        {' '}— agent: {f.locations[0].agent_name}
                                      </Typography>
                                    )}
                                  </Typography>
                                </Box>
                              )}
                              {f.finding_kind === 'escalation_path' && f.escalation_path && f.escalation_path.length > 1 && (
                                <Box sx={{ mt: 1, mb: 1, p: 1.5, bgcolor: 'error.50', borderRadius: 1, borderLeft: 3, borderColor: 'error.main' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Escalation path
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.5, mb: 1 }}>
                                    {f.escalation_path.map((hint: string, idx: number) => (
                                      <Box key={hint} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Chip
                                          label={agentNameByHint[hint] || hint}
                                          size="small"
                                          color={idx === f.escalation_path!.length - 1 ? 'error' : 'default'}
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem' }}
                                        />
                                        {idx < f.escalation_path!.length - 1 && (
                                          <Typography variant="caption" color="text.secondary">→</Typography>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                  <Button
                                    size="small" variant="outlined" color="error"
                                    onClick={() => { setTopologyFocusPath(f.escalation_path!); setDetailTab(4) }}
                                  >
                                    View escalation path in topology →
                                  </Button>
                                </Box>
                              )}
                              {/* The "Best practice" block that used to sit here
                                  rendered the matched knowledge-base rule's
                                  remediation, directly beneath the recommended
                                  action. It is gone because a finding must carry
                                  one instruction, not two.

                                  Measured on one repository's scan: 83 findings
                                  showed both blocks and all 83 said the same
                                  words twice. After the analyzer's own advice
                                  was given precedence over the knowledge-base
                                  match, 44 still duplicated and 14 began showing
                                  two *different* instructions for one defect —
                                  which is worse than redundancy, because a
                                  reader has no way to tell which to follow and
                                  the page offers no reason to prefer either.

                                  The precedence decision is made once, on the
                                  server, where the finding is written. Offering
                                  the runner-up alongside it invites the reader
                                  to overturn a decision they cannot see the
                                  basis for.

                                  The block carried one thing worth keeping — how
                                  strong the knowledge-base match was — and that
                                  has moved to the provenance footer below, where
                                  it describes where the finding came from rather
                                  than sitting beside advice it no longer
                                  explains. */}
                              {/* The provenance footer that sat here named the
                                  detector ("Semantic analyzer"), the emitting
                                  rule (`REL_LC003`) and the matched
                                  knowledge-base rule with its similarity
                                  ("KB-SEC-MAE-L7-001 44%").

                                  All three describe how ARIAS is built. A
                                  customer cannot act on any of them — the
                                  title, description, recommendation and code
                                  examples above are the actionable part — and
                                  together they disclose the shape of the rule
                                  set. Every finding on two production scans
                                  carried one, and not one was a rule the
                                  tenant owned.

                                  Nothing was deleted: `analyzer_source`,
                                  `native_rule_id`, `rule_id` and
                                  `kb_similarity` are still written on every
                                  finding, which is what keeps the question
                                  answerable when somebody asks why a finding
                                  reads the way it does. The API withholds them
                                  from this response instead, and returns an
                                  opaque `rule_group` so identical findings
                                  still collapse into one row. A rule the
                                  tenant authored is still named, because that
                                  one is theirs. */}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {trialCap?.is_capped && (
              <UpgradeGate
                totalQualifying={trialCap.total_qualifying}
                visibleQualifying={trialCap.visible_qualifying}
              />
            )}
          </CardContent>
        </Card>
        )
      })()}

      {/* Analysis Summary (in findings tab) */}
      {analysis.summary && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Summary</Typography>
            <Typography variant="body2">{analysis.summary}</Typography>
          </CardContent>
        </Card>
      )}
      </Box>)}

      {/* Tab 2: Governance */}
      {detailTab === 2 && (<Box>
        {/* Policy Gaps */}
        {analysis.policy_gaps?.length > 0 && analysis.policy_gaps[0]?.category !== 'aligned' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Warning sx={{ mr: 1, verticalAlign: 'middle', color: '#ff9800' }} />
                Policy-Code Gaps ({analysis.policy_gaps.filter((g: any) => g.category !== 'aligned').length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Misalignments between your governance documentation and what the code actually does.
              </Typography>
              {analysis.policy_gaps.filter((g: any) => g.category !== 'aligned').map((gap: any, i: number) => (
                <Box key={i} sx={{ p: 2, mb: 1.5, borderRadius: 1, border: 1,
                  borderColor: gap.severity === 'critical' ? 'error.main' : gap.severity === 'high' ? 'warning.main' : 'grey.300',
                  bgcolor: gap.severity === 'critical' ? 'error.50' : gap.severity === 'high' ? 'warning.50' : 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Chip label={gap.category?.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize', fontWeight: 600 }} />
                    <Chip label={gap.severity} size="small" color={getSeverityColor(gap.severity) as any} />
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>What your documentation says</Typography>
                    <Typography variant="body2">{gap.policy_claim}</Typography>
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>What the code shows</Typography>
                    <Typography variant="body2" color="error.main">{gap.code_reality}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Recommendation</Typography>
                    <Typography variant="body2">{gap.recommendation}</Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
        {analysis.policy_gaps?.length === 1 && analysis.policy_gaps[0]?.category === 'aligned' && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, '&:last-child': { pb: 2 } }}>
              <CheckCircle sx={{ color: '#4caf50', fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>No Policy-Code Gaps Detected</Typography>
                <Typography variant="body2" color="text.secondary">{analysis.policy_gaps[0].policy_claim}</Typography>
              </Box>
            </CardContent>
          </Card>
        )}
        {/* Document Evidence */}
        {analysis.document_evidence?.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Article sx={{ mr: 1, verticalAlign: 'middle' }} />
                Document References ({analysis.document_evidence.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Governance-related documents found during the scan.
              </Typography>
              <Grid container spacing={1.5}>
                {analysis.document_evidence.map((doc: any, i: number) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="subtitle2" noWrap>{doc.file_path?.split('/').pop() || 'Document'}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                          <Chip label={doc.file_type} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                          <Chip label={doc.classification} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        </Box>
                        {doc.governance_categories?.length > 0 && (
                          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                            {doc.governance_categories.map((cat: string) => (
                              <Chip key={cat} label={cat.replace(/_/g, ' ')} size="small" color="success" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}
        {!analysis.policy_gaps?.length && !analysis.document_evidence?.length && (
          <Card><CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No governance documents or policy gaps detected. Include governance docs (policies, runbooks) in your repo for policy-code gap analysis.
            </Typography>
          </CardContent></Card>
        )}
      </Box>)}

      {/* Tab 3: Export */}
      {detailTab === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Download sx={{ mr: 1, verticalAlign: 'middle' }} />
              Export & Reports
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Description sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>Generate Report</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Select sections to include:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: -1 }}>
                      {[
                        { key: 'summary', label: 'Executive Summary' },
                        { key: 'maturity', label: 'Maturity Scores' },
                        { key: 'agents', label: 'Agent Details' },
                        { key: 'findings', label: 'Findings' },
                        { key: 'remediation', label: 'Remediation Plan' },
                        { key: 'compliance', label: 'Compliance Mapping' },
                      ].map(s => (
                        <FormControlLabel key={s.key} sx={{ height: 28 }}
                          control={<Checkbox size="small" checked={(reportSections as any)[s.key]}
                            onChange={e => setReportSections({ ...reportSections, [s.key]: e.target.checked })} />}
                          label={<Typography variant="body2">{s.label}</Typography>} />
                      ))}
                    </Box>
                    <Button variant="contained" fullWidth onClick={handleGenerateReport} disabled={generating} sx={{ mt: 1 }}>
                      {generating ? 'Generating...' : 'Generate Report'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Assessment sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>Export Analysis JSON</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Raw analysis data including all scores, agents, and findings.
                    </Typography>
                    <Button variant="outlined" onClick={() => {
                      const blob = new Blob([JSON.stringify({ analysis, findings }, null, 2)], { type: 'application/json' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url; a.download = `arias-analysis-${analysisId?.substring(0, 8)}.json`; a.click()
                      URL.revokeObjectURL(url)
                    }}>
                      Download JSON
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <SmartToy sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                    <Typography variant="subtitle2" gutterBottom>Export Agent Details</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      CSV of all agents with patterns, tools, guardrails, and ABF status.
                    </Typography>
                    <Button variant="outlined" onClick={() => {
                      const agents = analysis.agent_inventory || []
                      const headers = 'Name,Framework,Pattern,Role,Model,Autonomy,Memory,Tools,Guardrails,File\n'
                      const rows = agents.map((a: any) =>
                        [a.agent_name, a.framework, a.architecture_pattern, a.role, a.llm_model,
                         a.autonomy, a.memory_type, (a.tools||[]).join(';'), (a.guardrails||[]).join(';'), a.file_path
                        ].map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(',')
                      ).join('\n')
                      const blob = new Blob([headers + rows], { type: 'text/csv' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url; a.download = `arias-agents-${analysisId?.substring(0, 8)}.csv`; a.click()
                      URL.revokeObjectURL(url)
                    }}>
                      Download CSV
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              {/* Telemetry Gaps — an export rather than a view, because that
                  is the form its reader receives it in. A detection engineer
                  takes the list, opens a ticket per requirement and works it
                  over a quarter; nobody watches it. Self-gating: the card
                  reports that it is part of Exposure if the route refuses. */}
              <Grid item xs={12} sm={6} md={4}>
                <TelemetryGapsExport analysisId={analysisId} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tab 4: Topology — §16 V1 mesh, single-scan scope */}
      {detailTab === 4 && showTopology && (
        <AnalysisTopologyTab
          analysisId={analysisId || ''}
          onViewAgentFindings={(agentId, agentName) => {
            setFindingsAgentFilter({ agentId, agentName })
            setDetailTab(1)
          }}
          focusEscalationPath={topologyFocusPath}
        />
      )}


      {/* Feedback dialog (Phase 1 of the continuous-improvement loop).
          Captures false positive / helpful / severity-too-high / unclear
          plus an optional reason. The backend auto-captures everything
          else from the finding lookup — rule_id, framework, agent context,
          versions — so this dialog stays minimal. */}
      <Dialog
        open={feedbackDialog !== null}
        onClose={() => setFeedbackDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {feedbackDialog?.existingId ? 'Update feedback' : 'Mark feedback on this finding'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Your feedback helps tune ARIAS rules — both for your tenant and (anonymized) across the platform. Other context (rule, framework, agent) is captured automatically.
          </Typography>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel sx={{ fontSize: '0.85rem', mb: 1 }}>Feedback type</FormLabel>
            <RadioGroup value={feedbackType} onChange={e => setFeedbackType(e.target.value)}>
              <FormControlLabel value="false_positive" control={<Radio size="small" />}
                label={<><strong>False positive</strong> — this finding doesn't apply</>}
              />
              <FormControlLabel value="helpful" control={<Radio size="small" />}
                label={<><strong>Helpful</strong> — this finding caught a real issue</>}
              />
              <FormControlLabel value="severity_too_high" control={<Radio size="small" />}
                label={<><strong>Severity too high</strong> — real issue but lower priority</>}
              />
              <FormControlLabel value="unclear" control={<Radio size="small" />}
                label={<><strong>Unclear</strong> — couldn't tell if this applies</>}
              />
            </RadioGroup>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            label="Reason (optional)"
            placeholder="Why? — extra context helps tune the rule for everyone."
            value={feedbackReason}
            onChange={e => setFeedbackReason(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText={`${feedbackReason.length} / 2000`}
          />
        </DialogContent>
        <DialogActions>
          {feedbackDialog?.existingId && (
            <Button onClick={undoFeedback} color="error" disabled={feedbackSubmitting}>
              Remove
            </Button>
          )}
          <Button onClick={() => setFeedbackDialog(null)} disabled={feedbackSubmitting}>
            Cancel
          </Button>
          <Button onClick={submitFeedback} variant="contained" disabled={feedbackSubmitting}>
            {feedbackDialog?.existingId ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* What the customer decided about a finding.
          This is the half that reaches compliance: a "does not apply"
          decision stops the finding counting against a control, recomputes
          posture immediately, and travels to their GRC tool on its next poll.
          The decision attaches to the finding's cross-scan identity, so it
          survives re-scanning — which is exactly when it matters. */}
      <Dialog open={!!decisionDialog} onClose={() => setDecisionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Record your decision</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {decisionDialog?.title}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Decision</InputLabel>
            <Select
              value={decisionStatus}
              label="Decision"
              onChange={e => setDecisionStatus(e.target.value)}
            >
              {DECISION_STATUSES.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {DECISION_STATUSES.find(s => s.value === decisionStatus)?.help}
          </Typography>

          {decisionStatus === 'not_affected' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Why it does not apply</InputLabel>
              <Select
                value={decisionJustification}
                label="Why it does not apply"
                onChange={e => setDecisionJustification(e.target.value)}
              >
                {VEX_JUSTIFICATIONS.map(j => (
                  <MenuItem key={j.value} value={j.value}>{j.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth multiline rows={3} size="small"
            label="Notes (optional)"
            placeholder="Anything an auditor should know alongside the reason above."
            value={decisionStatement}
            onChange={e => setDecisionStatement(e.target.value)}
          />
          <Alert severity="info" variant="outlined" sx={{ mt: 2 }} icon={false}>
            <Typography variant="caption">
              This is recorded against your compliance evidence and included in
              exports to your GRC tool. It stays attached to this finding when
              you re-scan.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionDialog(null)}>Cancel</Button>
          <Button onClick={submitDecision} variant="contained" disabled={decisionSubmitting}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AnalysisDetailPage

/**
 * §16 V1 Preps 2/3/4 — Connectivity chip row for agent cards.
 *
 * Shows MCP/A2A/delegation presence with counts + tooltip drill-downs.
 * Each chip surfaces a different protocol channel; rendering only
 * fires when the field has content so cards without these signals
 * look identical to the pre-enrichment shape (additive-only invariant).
 *
 * Sources (all populated by scanner-client/framework_detector +
 * mcp_detector / a2a_detector / delegation_detector):
 *   mcp_servers[]    — agent IS an MCP server (advertised_tools list)
 *   mcp_clients[]    — agent CALLS MCP servers (server_name_ref list)
 *   a2a_endpoints[]  — agent SERVES A2A (paths list)
 *   a2a_calls[]      — agent CALLS A2A endpoints (target_url list)
 *   delegates_to[]   — agent DELEGATES to other agents (target_agent_name list)
 *
 * delegates_to is raw scanner-detected output — a target name can be a
 * graph-node identifier (e.g. LangGraph add_edge) with no corresponding
 * agent record in this scan's inventory. The Topology/Mesh view only draws
 * an edge for names that resolve (services/analysis/mesh.py
 * _build_delegation_edges); this chip mirrors that same resolution so an
 * unresolved target isn't presented as if it were a real agent.
 */
// Fold a name to a case/format-insensitive key for delegation matching —
// mirrors services/analysis/mesh.py's _normalize_agent_key so this chip's
// resolved/unresolved count agrees with the Topology view's edges. Without
// this, a delegation target written as a human-readable label (e.g.
// CrewAI's Task(agent=x) resolving to role= text "Remediation Strategist")
// never matches the agent's own code-derived identity
// ("remediation_strategist") in ANY repo that has this pattern.
const normalizeAgentKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

function AgentConnectivityChips({ agent, knownAgents }: { agent: any; knownAgents?: any[] }) {
  const mcpServers = (agent.mcp_servers || []) as Array<{ name: string; advertised_tools?: string[]; transport?: string }>
  const mcpClients = (agent.mcp_clients || []) as Array<{ server_name_ref: string; transport?: string; source?: string }>
  const a2aEndpoints = (agent.a2a_endpoints || []) as Array<{ path: string; method?: string; advertised_capabilities?: string[] }>
  const a2aCalls = (agent.a2a_calls || []) as Array<{ target_url: string; method?: string }>
  const delegates = (agent.delegates_to || []) as Array<{ target_agent_name: string; target_framework?: string; confidence?: number }>
  const knownAgentNames = new Set<string>()
  ;(knownAgents || []).forEach((a: any) => {
    if (a.agent_name) knownAgentNames.add(normalizeAgentKey(a.agent_name))
    if (a.code_identifier) knownAgentNames.add(normalizeAgentKey(a.code_identifier))
  })
  const unresolvedDelegates = delegates.filter(d => !knownAgentNames.has(normalizeAgentKey(d.target_agent_name))).length

  const hasAny =
    mcpServers.length > 0 || mcpClients.length > 0 ||
    a2aEndpoints.length > 0 || a2aCalls.length > 0 ||
    delegates.length > 0
  if (!hasAny) return null

  // De-dupe __unknown__ MCP refs from the count display — they're a
  // detector signal, not a meaningful linkage from the user's POV.
  const namedClients = mcpClients.filter(c => c.server_name_ref && c.server_name_ref !== '__unknown__')

  return (
    <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {mcpServers.length > 0 && (
        <Tooltip
          title={
            <Box sx={{ fontSize: 11 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                MCP server{mcpServers.length > 1 ? 's' : ''} hosted
              </Typography>
              {mcpServers.map((s, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                  {s.name}
                  {s.transport ? ` · ${s.transport}` : ''}
                  {s.advertised_tools?.length ? ` · ${s.advertised_tools.length} tools` : ''}
                </Typography>
              ))}
            </Box>
          }
        >
          <Chip
            icon={<Storage sx={{ fontSize: 14 }} />}
            label={`MCP server${mcpServers.length > 1 ? ` × ${mcpServers.length}` : ''}${mcpServers[0]?.advertised_tools?.length ? ` · ${mcpServers[0].advertised_tools.length} tools` : ''}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Tooltip>
      )}
      {namedClients.length > 0 && (
        <Tooltip
          title={
            <Box sx={{ fontSize: 11 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                MCP servers called
              </Typography>
              {namedClients.map((c, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                  {c.server_name_ref}
                  {c.transport ? ` · ${c.transport}` : ''}
                  {c.source === 'config' ? ' · via config' : c.source === 'code' ? ' · via code' : ''}
                </Typography>
              ))}
            </Box>
          }
        >
          <Chip
            icon={<CallMade sx={{ fontSize: 14 }} />}
            label={`MCP client → ${namedClients.length}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Tooltip>
      )}
      {a2aEndpoints.length > 0 && (
        <Tooltip
          title={
            <Box sx={{ fontSize: 11 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                A2A endpoints served
              </Typography>
              {a2aEndpoints.slice(0, 6).map((e, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                  {e.method || 'POST'} {e.path}
                </Typography>
              ))}
              {a2aEndpoints.length > 6 && (
                <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                  +{a2aEndpoints.length - 6} more
                </Typography>
              )}
            </Box>
          }
        >
          <Chip
            icon={<Public sx={{ fontSize: 14 }} />}
            label={`A2A · ${a2aEndpoints.length} endpoint${a2aEndpoints.length > 1 ? 's' : ''}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Tooltip>
      )}
      {a2aCalls.length > 0 && (
        <Tooltip
          title={
            <Box sx={{ fontSize: 11 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                A2A endpoints called
              </Typography>
              {a2aCalls.slice(0, 6).map((c, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
                  {c.target_url}
                </Typography>
              ))}
              {a2aCalls.length > 6 && (
                <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                  +{a2aCalls.length - 6} more
                </Typography>
              )}
            </Box>
          }
        >
          <Chip
            icon={<CallReceived sx={{ fontSize: 14 }} />}
            label={`A2A client → ${a2aCalls.length}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Tooltip>
      )}
      {delegates.length > 0 && (
        <Tooltip
          title={
            <Box sx={{ fontSize: 11 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                Delegates to
              </Typography>
              {delegates.map((d, i) => {
                const resolved = knownAgentNames.has(normalizeAgentKey(d.target_agent_name))
                return (
                  <Typography key={i} variant="caption" sx={{ display: 'block', fontStyle: resolved ? 'normal' : 'italic', opacity: resolved ? 1 : 0.7 }}>
                    {d.target_agent_name}
                    {d.target_framework ? ` · ${d.target_framework}` : ''}
                    {d.confidence != null ? ` · ${(d.confidence * 100).toFixed(0)}%` : ''}
                    {!resolved ? ' · not found in this scan' : ''}
                  </Typography>
                )
              })}
            </Box>
          }
        >
          <Chip
            icon={<AltRoute sx={{ fontSize: 14 }} />}
            label={`Delegates → ${delegates.length}${unresolvedDelegates > 0 ? ` (${unresolvedDelegates} unresolved)` : ''}`}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Tooltip>
      )}
    </Box>
  )
}

/**
 * §16 V1 Mesh — single-analysis Topology tab.
 *
 * Renders the mesh for one scan only (scope=analysis). The shared
 * MeshView component drives the canvas; this wrapper handles the
 * data fetch, the click-to-drawer flow, and the soft refresh on
 * annotation save.
 */
function AnalysisTopologyTab({
  analysisId,
  onViewAgentFindings,
  focusEscalationPath = null,
}: {
  analysisId: string
  onViewAgentFindings?: (agentId: string, agentName: string) => void
  // Escalation-path UI fix (2026-07-29) — ordered agent_id_hints from
  // the Findings tab's "View escalation path" CTA, passed straight
  // through to MeshView/NodeDetailDrawer.
  focusEscalationPath?: string[] | null
}) {
  const navigate = useNavigate()
  const [mesh, setMesh] = useState<MeshResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedNode, setSelectedNode] = useState<MeshNode | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  // M5b — Compliance Lens state
  const [overlay, setOverlay] = useState<OverlayMode>('risk')
  const [complianceFramework, setComplianceFramework] = useState<string | null>(null)
  const [availableFrameworks, setAvailableFrameworks] = useState<Array<{ key: string; display_name?: string }>>([])

  useEffect(() => {
    if (!analysisId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    const params: { lens?: string; framework?: string } = {}
    if (overlay === 'compliance' && complianceFramework) {
      params.lens = 'compliance'
      params.framework = complianceFramework
    }
    analysisApi
      .getAnalysisMesh(analysisId, params)
      .then(res => {
        if (!cancelled) setMesh(res.data as MeshResponse)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (e as { message?: string })?.message ||
          'Failed to load topology'
        setError(detail)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [analysisId, reloadKey, overlay, complianceFramework])

  // Framework list for the Compliance overlay picker
  useEffect(() => {
    let cancelled = false
    analysisApi
      .getComplianceFrameworks()
      .then((res) => {
        if (cancelled) return
        const list = (res.data?.frameworks || []) as Array<{ framework: string; display_name?: string }>
        setAvailableFrameworks(list.map((f) => ({ key: f.framework, display_name: f.display_name })))
      })
      .catch(() => { /* compliance posture may not be set up — leave list empty */ })
    return () => { cancelled = true }
  }, [analysisId])

  // Memoised, not a literal. This is passed as `mesh` while the fetch is in
  // flight or after it fails, and MeshView memoises on the identity of that
  // prop — a fresh object each render re-runs its layout effect, which
  // re-renders, which builds another one. Same shape as the NO_REPO_FILTER
  // default over in MeshView, and the same symptom: a view that flickers
  // instead of settling.
  const empty: MeshResponse = useMemo(() => ({
    scope: 'analysis',
    scope_id: analysisId,
    nodes: [],
    edges: [],
    repos: [],
    meta: { scan_count: 0, agent_count: 0, synthetic_count: 0, edge_count: 0, annotations_count: 0 },
  }), [analysisId])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
        <Chip
          icon={<Hub sx={{ fontSize: 14 }} />}
          label="This scan only"
          size="small"
          color="default"
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
        <Typography variant="caption" color="text.secondary">
          Showing the topology for this single scan. To compare against the
          tenant-wide aggregated view, visit the Topology page.
        </Typography>
      </Stack>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          height: 'calc(100vh - 240px)',
          minHeight: 520,
        }}
      >
        <MeshView
          mesh={mesh || empty}
          loading={loading}
          error={error}
          height="100%"
          onNodeClick={setSelectedNode}
          overlay={overlay}
          onOverlayChange={setOverlay}
          complianceFramework={complianceFramework}
          availableFrameworks={availableFrameworks}
          onComplianceFrameworkChange={setComplianceFramework}
          focusPath={focusEscalationPath}
        />
      </Box>
      <NodeDetailDrawer
        node={selectedNode}
        analysisId={analysisId}
        onClose={() => setSelectedNode(null)}
        onAnnotationSaved={() => setReloadKey(k => k + 1)}
        overlay={overlay}
        nodes={mesh?.nodes || []}
        edges={mesh?.edges || []}
        complianceFramework={complianceFramework}
        onViewFindings={(agentId) => {
          const target = (mesh?.nodes || []).find(n => n.id === agentId)
          onViewAgentFindings?.(agentId, target?.name || agentId.slice(0, 8))
          setSelectedNode(null)
        }}
        onOpenCertTimeline={(certId) => {
          setSelectedNode(null)
          navigate(`/certificates/${certId}`)
        }}
        escalationPath={focusEscalationPath}
      />
    </Box>
  )
}
